/**
 * English (EN) — primary locale for VistoChat dashboard since 2026-05-03.
 *
 * Structure mirrors the page hierarchy:
 *   common.*       — buttons, generic words, status labels reused everywhere
 *   nav.*          — sidebar + topbar
 *   auth.*         — Login / Signup / Verify / AcceptInvite / SsoDone
 *   dashboard.*    — DashboardView
 *   bots.*         — BotsView + BotEditView (9 tabs)
 *   conversations.*
 *   leads.*
 *   knowledge.*
 *   team.*
 *   billing.*
 *   stats.*
 *   account.*
 *   settings.*
 *   admin.*        — AdminStats / AdminManaged / AdminUsage / AdminContactRequests
 *   errors.*       — surface-level error messages (network, auth, validation)
 *   notFound.*     — 404 view
 *
 * KEEP IN SYNC with el.ts. When adding a new key here, add it there too.
 */
export default {
  common: {
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    loading: 'Loading…',
    refresh: 'Refresh',
    copy: 'Copy',
    copied: 'Copied',
    undo: 'Undo',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'required',
    search: 'Search',
    searchPlaceholder: 'Search…',
    none: 'None',
    all: 'All',
    selectOption: 'Select an option',
    notAvailable: 'N/A',
    learnMore: 'Learn more',
    viewAll: 'View all',
    viewMore: 'View more',
    viewLess: 'View less',
    showAdvanced: 'Show advanced',
    hideAdvanced: 'Hide advanced',
    apply: 'Apply',
    submit: 'Submit',
    retry: 'Retry',
    download: 'Download',
    upload: 'Upload',
    enabled: 'Enabled',
    disabled: 'Disabled',
    active: 'Active',
    inactive: 'Inactive',
    draft: 'Draft',
    paused: 'Paused',
    archived: 'Archived',
    online: 'Online',
    offline: 'Offline',
    connect: 'Connect',
    disconnect: 'Disconnect',
    connected: 'Connected',
    notConnected: 'Not connected',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    success: 'Success',
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    last365Days: 'Last 365 days',
    monthly: 'Monthly',
    annual: 'Annual',
    yearly: 'Yearly',
    perMonth: '/month',
    perYear: '/year',
  },
  nav: {
    companies: 'Companies',
    dashboard: 'Dashboard',
    bots: 'Bots',
    brains: 'Brains',
    conversations: 'Conversations',
    leads: 'Leads',
    knowledge: 'Knowledge (RAG)',
    plugins: 'Plugins',
    stats: 'Stats',
    team: 'Team',
    billing: 'Subscription',
    settings: 'Settings',
    account: 'Account',
    admin: 'Admin',
    adminStats: 'System stats',
    adminManaged: 'Managed subs',
    adminCompanies: 'Companies',
    adminFeedback: 'Customer feedback',
    adminUsers: 'Users',
    adminUsage: 'Usage & costs',
    adminContactRequests: 'Contact requests',
    notifications: 'Notifications',
    pushEnable: 'Enable push notifications (new leads + human requests)',
    pushDisable: 'Push notifications ON — click to disable',
    logout: 'Log out',
    profile: 'Profile',
    language: 'Language',
    languageEn: 'English',
    languageEl: 'Greek',
    languageSwitch: 'Switch language',
    helpCenter: 'Help center',
    statusPage: 'Status',
    legalDocs: 'Legal',
    skipToContent: 'Skip to content',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    primaryNav: 'Primary navigation',
    accountAria: 'Account',
    partnerBadge: 'Partner',
    partnerBadgeTitle: '{n} paid bots — Partner Program',
    sandboxLabel: 'Sandbox · Bun + Hono',
    upgrade: 'Upgrade',
    groups: {
      partner: 'Partner network',
      overview: 'Overview',
      manage: 'Manage',
      admin: 'Admin (Vistoweb)',
      account: 'Account',
    },
  },
  banner: {
    verifyEmailTitle: 'Verify your email',
    verifyEmailBody: 'Check your inbox for the link we sent to {email}',
    resend: 'Resend',
    resending: 'Sending…',
    resendSuccess: 'Verification email sent. Check your inbox.',
    resendSentLabel: 'Sent ✓',
    resendFailure: 'Could not send verification email. Try again in a moment.',
  },
  footer: {
    company: 'Vistoweb E.E.',
    gemi: 'GEMI',
    afm: 'TIN',
    address: 'Address',
    legal: 'Legal',
    terms: 'Terms',
    privacy: 'Privacy',
    cookies: 'Cookies',
    refund: 'Refunds',
    aup: 'Acceptable use',
    dpa: 'DPA',
    subprocessors: 'Subprocessors',
    sla: 'SLA',
  },
  auth: {
    login: {
      title: 'Welcome back',
      subtitle: 'Sign in to your VistoChat account',
      tagline: 'The AI chatbot for ambitious websites.',
      email: 'Email',
      password: 'Password',
      passwordRequired: 'Enter your password.',
      remember: 'Remember me on this device',
      submit: 'Sign in',
      submitting: 'Signing in…',
      forgotPassword: 'Forgot your password?',
      noAccount: "Don't have an account?",
      signupLink: 'Create an account',
      or: 'or',
      ssoGoogle: 'Continue with Google',
      ssoMicrosoft: 'Continue with Microsoft',
      ssoTermsNoticePrefix: 'By continuing with Google or Microsoft you accept the',
      ssoTermsNoticeAnd: 'and the',
      errors: {
        invalid: 'Invalid email or password.',
        generic: 'Sign-in failed. Please try again.',
        ssoStateMismatch: 'SSO security error. Please try again.',
        ssoMissingCode: 'SSO sign-in was cancelled.',
        ssoMisconfigured: 'SSO is not configured for this provider.',
        ssoNoVerifiedTeam: "Your email doesn't match an approved team domain.",
      },
    },
    signup: {
      title: 'Create your account',
      subtitle: '{days}-day free trial of {plan}. No credit card needed.',
      subtitleInvite: 'Create an account to join the team that invited you.',
      tagline: 'The AI chatbot for ambitious websites.',
      fullName: 'Full name',
      fullNamePlaceholder: 'e.g. Jane Smith',
      fullNameRequired: 'Tell us your name.',
      teamName: 'Company name',
      teamNamePlaceholder: 'e.g. Acme Inc.',
      teamNameHelp: 'optional',
      email: 'Email',
      password: 'Password',
      passwordHelp: 'at least 8 characters',
      passwordRequired: 'Choose a password.',
      passwordTooShort: 'Password must be at least 8 characters.',
      passwordShow: 'Show',
      passwordHide: 'Hide',
      acceptTermsPrefix: 'I agree to the',
      acceptTermsTerms: 'Terms of Service',
      acceptTermsAnd: 'and the',
      acceptTermsPrivacy: 'Privacy Policy',
      submit: 'Create account',
      submitting: 'Creating account…',
      hasAccount: 'Already have an account?',
      loginLink: 'Sign in',
      orSignupWith: 'or sign up with',
      errors: {
        emailExists: 'An account with this email already exists.',
        validation: 'Check the form fields and try again.',
        passwordShort: 'Password must be at least 8 characters.',
        nameShort: 'Please enter your full name.',
        termsRequired: 'You must accept the Terms of Service to continue.',
        rateLimit: 'Too many signup attempts. Please try again later.',
        disposableEmail: 'Disposable email addresses are not allowed. Please use your real email.',
        generic: 'Signup failed. Please try again.',
      },
    },
    verify: {
      successTitle: 'Email verified',
      successBody: 'Your email is now confirmed. You have full access to every feature.',
      successCta: 'Continue to dashboard',
      expiredTitle: 'Link expired',
      expiredBody: 'Verification links are valid for 24 hours. Send a new one to keep going.',
      invalidTitle: 'Invalid link',
      invalidBody: "This verification link isn't valid or has already been used.",
      pendingTitle: 'Verify your email',
      pendingBody: 'Use the link we sent to your inbox to verify your account.',
      resend: 'Send a new link',
      resending: 'Sending…',
      resendSuccess: 'New email sent — check your inbox.',
      resendFailure: 'Could not send the link. Try again in a moment.',
      resendMustLogin: 'You need to sign in first to request a new link.',
      backToDashboard: 'Back to dashboard',
    },
    invite: {
      title: 'Team invitation',
      loadingPreview: 'Loading…',
      tokenMissing: 'Token missing.',
      loadError: 'Error loading invitation.',
      invalidTitle: 'Invitation not valid',
      statusExpired: 'This invitation has expired.',
      statusAccepted: 'This invitation has already been used.',
      statusInvalid: 'Invalid invitation.',
      backToVistoChat: '← Back to VistoChat',
      invitedAs: "You've been invited to be a {role} in the team {team}.",
      invitedFor: 'Invitation for {email}',
      invitedBy: 'from {inviter}',
      expiresOn: 'Expires {date}',
      acceptInstruction: 'Sign in with the email {email} to accept — or create a new account.',
      goToLogin: 'Sign in',
      createAccountCta: 'Create account',
      acceptCta: 'Accept invitation',
      accepting: 'Accepting…',
      acceptSuccess: 'Welcome to the team!',
      alreadyMember: "You're already a member of this team — invitation marked as accepted.",
      acceptFailure: 'Could not accept the invitation. Please try again.',
      emailMismatch: 'Sign in with {email} to accept this invitation.',
      differentAccountWarn: "You're signed in as {current}. This invitation is for {target}.",
      signInDifferent: 'Sign in with a different account',
    },
    ssoDone: {
      finishing: 'Finishing sign-in…',
    },
    demo: {
      loading: 'Loading the demo…',
      loadingSub: 'Setting you up with a sample workspace so you can explore the dashboard.',
      failed: 'Could not load the demo',
      goLogin: 'Back to sign-in',
    },
  },
  dashboard: {
    welcome: 'Welcome to VistoChat 👋',
    subtitle: 'Overview of your bots, conversations and leads.',
    newBotCta: '+ New bot',
    statsAriaLabel: 'Key metrics',
    stats: {
      activeBots: 'Active bots',
      conversations30d: 'Conversations (30d)',
      leads30d: 'Leads (30d)',
      conversion: 'Conversion (30d)',
      conversionMeta: 'leads / chats',
      deltaVs: 'vs previous 30d',
    },
    sections: {
      recentLeads: 'Recent leads',
      recentChats: 'Recent conversations',
      allLeads: 'All →',
      allChats: 'All →',
    },
    empty: {
      noLeads: 'No leads yet.',
      noChats: 'No conversations yet.',
      seeEmbed: 'See embed code →',
      addFirstBot: 'Add your first bot →',
    },
    list: {
      leadFallback: 'Unnamed lead',
      messagesCount: '{n} messages',
      leadPill: 'lead',
    },
  },
  notFound: {
    title: 'Page not found',
    body: "The page you're looking for doesn't exist or has been moved.",
    backHome: 'Back to dashboard',
  },
  demo: {
    banner: {
      text: "You're exploring a read-only demo workspace. Sign up free to keep your changes.",
      cta: 'Sign up free',
    },
    modal: {
      title: 'This is a demo workspace',
      body: "You're signed in as a guest, so changes can't be saved here. Sign up free to get your own workspace and start configuring real bots.",
      feature1: 'Free plan available — no credit card needed',
      feature2: 'Set up your bot in minutes with your own knowledge',
      feature3: 'Capture leads on your site from day one',
      signupCta: 'Sign up free',
      keepLooking: 'Keep exploring',
    },
  },
  errors: {
    network: 'Network error. Please check your connection and retry.',
    forbidden: "You don't have permission to perform this action.",
    notFound: 'Resource not found.',
    server: 'Something went wrong on our side. Please try again later.',
    unauthorized: 'Your session has expired. Please sign in again.',
    rateLimit: 'Too many requests. Please wait a moment and try again.',
  },
  validation: {
    required: 'This field is required.',
    emailInvalid: 'Invalid email address.',
    minLength: 'At least {n} characters.',
    maxLength: 'At most {n} characters.',
  },
  trial: {
    active: 'Trial · {days}d · {used}/{limit}',
    tooltip: 'Free trial: {days} days left, {used} of {limit} responses used',
    ended: 'Trial ended',
    boxActive: '{plan} trial: {days} days left · {used} of {limit} responses used.',
    boxActiveHint: 'Choose a plan any time — your assistant keeps answering without a break and everything you set up stays as it is.',
    boxExpired: 'The trial has ended — your assistant is no longer answering on your site.',
    boxExhausted: 'All {limit} trial responses have been used — your assistant is no longer answering on your site.',
    boxEndedHint: 'Everything you set up has been kept. Choose a plan and it starts answering again straight away.',
    sectionChoose: 'Choose a plan',
    activateBtn: 'Activate {plan}',
    pickOption: '{plan} — {days}-day free trial',
    pickHint: 'No card. {days} days or {responses} responses, whichever comes first — then you choose a plan.',
  },
  bots: {
    // ---- list view ----
    list: {
      heading: 'Bots',
      subtitle: 'Manage your chatbots, prompts and embed code.',
      subtitleLocked: 'View your bots and copy the embed code.',
      newBotCta: '+ New bot',
      emptyHeading: 'No bots yet',
      emptyBody: 'Create your first chatbot to get started.',
      emptyCta: '+ Create your first bot',
      langCount: '{n} language | {n} languages',
      searchPlaceholder: 'Search by name…',
      viewCards: 'Cards',
      viewList: 'List',
      noMatch: 'No bots match your search.',
      cols: { name: 'Name', status: 'Status', plan: 'Plan', model: 'Model', actions: 'Actions' },
      copyEmbedSuccess: 'Embed code copied to clipboard.\n\nLocales: {locales}',
      copyEmbedMultiNote: '(Multi-language: data-locale required in the embed)',
      copyEmbedError: 'Error: {msg}',
      cardActions: {
        view: '👁 View',
        settings: '⚙ Settings',
        preview: 'Preview',
        embed: 'Embed code',
      },
      statusBadges: {
        pastDue: 'past due',
        cancelling: 'cancels at period end',
        managedExt: 'manual billing',
      },
    },
    // ---- create modal (BotsView) ----
    create: {
      title: 'New bot',
      ariaTitle: 'New bot',
      closeAria: 'Close',
      cancel: 'Cancel',
      submit: 'Create bot',
      submitting: 'Creating…',
      companyLabel: 'Company',
      companyHint: 'The bot belongs to this company: its login, its conversations and its invoice line.',
      companyOwn: '(yours)',
      companyNewOption: '+ New company…',
      companyNewName: 'New company name',
      companyNewParent: 'Under',
      companyNewHint: 'The company is created together with the bot. You can add invoicing details and invite the customer from the Companies page.',
      planLabel: 'Plan',
      planFree: 'Free — no charge while you set it up',
      planPriced: '{plan} — {price} / month',
      planNoPrice: '{plan} — no price in your price list yet',
      planHint: 'Start on Free while the bot is being set up; it is switched to Pro or Max once it is installed at the customer. The price is frozen on the bot from your price list.',
      nameLabel: 'Name',
      namePlaceholder: 'e.g. Azur Hotel',
      nameRequired: 'Give your bot a name.',
      displayNameLabel: 'Bot name shown in the widget (optional)',
      displayNamePlaceholder: 'e.g. Azur Assistant',
      defaultLocaleLabel: 'Default language',
      defaultLocaleOptions: {
        el: 'Greek (el)',
        en: 'English (en)',
        de: 'German (de)',
        fr: 'French (fr)',
        it: 'Italian (it)',
        es: 'Spanish (es)',
      },
      startMessageLabel: 'Welcome message (start message)',
      promptLabel: 'System prompt (shared across all languages)',
      promptDefault: 'You are a customer-support assistant for our business. Answer politely and help customers with their questions.',
      startMessageDefault: 'How can I help you?',
      colorLabel: 'Color',
      positionLabel: 'Position',
      positions: {
        bottomRight: 'Bottom right',
        bottomLeft: 'Bottom left',
        bottomCenter: 'Bottom center',
        topRight: 'Top right',
        topLeft: 'Top left',
        topCenter: 'Top center',
      },
      launcherStyleLabel: 'Launcher style',
      launcherStyles: {
        bubble: 'Bubble (classic)',
        pill: 'Pill (CTA)',
        preview: 'Preview (mini chat)',
      },
      errorCreate: 'Error creating bot',
    },
    // ---- common ----
    loading: 'Loading…',
    loadError: 'Loading error',
    saveError: 'Save error',
    deleteError: 'Delete error',
    reindexError: 'Reindex error',
    // ---- edit page ----
    edit: {
      backAria: 'Path',
      backToBots: 'Back to Bots',
      unsavedDot: '● Unsaved changes',
      unsavedTooltip: 'Cmd/Ctrl+S to save',
      previewBtn: 'Preview',
      saveBtn: 'Save changes',
      saving: 'Saving…',
      closeBtn: 'Close',
      overview: {
      title: 'Brain overview',
      managedBy: 'This bot is set up and maintained by your provider.',
      name: 'Name',
      status: 'Status',
      languages: 'Languages',
      installedOn: 'Installed on',
      changesTitle: 'Need a change?',
      changesBody: 'For changes to the wording, the knowledge base or the appearance, contact your provider. Your conversations, leads and stats always stay available to you.',
    },
    tabs: {
        overview: 'Overview',
      general: 'General',
        prompt: 'Prompt & Model',
        widget: 'Widget',
        access: 'Access',
        translations: 'Languages',
        knowledge: 'Knowledge (RAG)',
        embed: 'Embed code',
        limits: 'Limits',
        plan: 'Plan & Billing',
        ariaLabel: 'Bot settings tabs',
        unsavedAria: 'unsaved changes',
        unsavedTitle: 'unsaved changes',
        settingsAria: 'Bot status',
      },
      status: {
        active: 'Active',
        draft: 'Draft',
        paused: 'Paused',
        descActive: 'The widget answers real visitors',
        descPaused: 'The widget is silent — shows a maintenance message',
        descDraft: 'Test mode — widget appears only in dashboard previews',
        unsavedConfirm: 'You have unsaved changes. Leave without saving?',
      },
      // ---- General tab ----
      general: {
        sectionBasics: 'Basics',
        nameInternal: 'Name (internal)',
        sectionBehavior: 'Behavior',
        contentAware: 'Content Aware',
        contentAwareMeta: 'Sends the assistant the page the visitor is on (address, title, text), so it can answer about "this one here". Automatic — nothing to write in the instructions.',
        localMemory: 'Local Memory',
        localMemoryMeta: 'Stores the conversation in the visitor browser — auto-resumes on return.',
        sectionPlaceholders: 'Logged-in user details in the instructions',
        placeholdersAuthenticated: 'When the visitor is logged in to your system, you can write {names} in the instructions — they are replaced with that person\'s details in every conversation.',
        sectionNotifications: 'Notifications',
        notifEmailsLabel: 'Email recipients for lead notifications (one per line)',
        notifLocaleLabel: 'Notification language',
        notifLocaleSite: 'Same as the site language',
        notifLocaleHint: 'Language of the emails we send to you (new lead, visitor asked for a person) — independent of the language of your site.',
        sectionDelete: 'Delete bot',
        deleteMeta: 'The bot is archived. Data stays in the database.',
        deleteBtn: 'Delete',
        deleteConfirm: 'Delete the bot "{name}"? (it will be archived — data is not lost)',
      },
      // ---- Prompt & Model tab ----
      prompt: {
        instructionsLabel: 'Instructions (shared across all languages)',
        extraContextLabel: 'Additional context (optional)',
        providerLabel: 'AI provider',
        providers: {
          gemini: 'Gemini (Google)',
          deepseek: 'DeepSeek — cheap + powerful (China hosting)',
          openai: 'OpenAI',
          anthropic: 'Anthropic (Claude)',
        },
        modelLabel: 'Model',
        modelHelpListOrCustom: 'Pick from the list or "Custom" to enter your own model name (e.g. preview/beta releases).',
        customModelPrompt: 'Enter the model name (e.g. gemini-experimental-1206):',
        modelOptionCustom: 'Custom — type your own',
        deepseekGdprWarning: '⚠️ GDPR: DeepSeek is hosted in China. If your bot serves EU visitors, update your site\'s privacy policy.',
        sectionParams: 'Parameters',
        temperatureLabel: 'Temperature',
        temperatureMeta: 'Higher = more creative. Default 0.45.',
        maxTokensLabel: 'Max tokens',
        maxTokensMeta: 'Longest response the AI can generate. Default 512.',
        replyStyleLabel: 'Reply length (platform rule)',
        replyStyleConcise: 'Concise (default)',
        replyStyleBrief: 'Very short (1-3 sentences)',
        replyStyleOff: 'No platform rule (the prompt sets the voice)',
        languageModeLabel: 'Reply language (platform rule)',
        languageModeVisitor: "In the language of each visitor message",
        languageModeLocale: "Always in the widget's language",
        languageModeOff: 'No platform rule',
        languageModeMeta: "Visitor's language: the server detects each message's language and asks for it explicitly at the end of the prompt (when unsure, it forces nothing). No rule: the Brain's prompt decides. Visible to Vistoweb only.",
        leadAskLabel: 'When it asks for contact details',
        leadAskIntent: "On buying/booking/quote intent + when it can't help",
        leadAskHandoff: "Only when it can't help",
        leadAskNever: 'Never (keeps only what the visitor volunteers)',
        leadAskMeta: "'When/how' is central; 'which details and what for' stays in the instructions. Visible to Vistoweb only.",
        personaPolicyLabel: 'Identity & AI disclosure',
        personaPolicyPlatform: "Platform policy (introduce by name; honest 'digital assistant')",
        personaPolicyOff: 'No platform rule (demo only)',
        personaPolicyMeta: 'Name and role live in the instructions; the disclosure policy is central. Visible to Vistoweb only.',
        replyStyleMeta: 'The length rule lives centrally — do NOT write it into the prompt again. Visible to Vistoweb only.',
        maxHistoryLabel: 'Max history (messages)',
        maxHistoryMeta: 'How many previous turns to send. Default 10.',
        models: {
          geminiFlashLite: 'Gemini Flash Lite (latest) — cheap, default',
          gemini25FlashLite: 'Gemini 2.5 Flash Lite — cheap stable version',
          gemini20Flash: 'Gemini 2.0 Flash — fast, balanced',
          gemini25Flash: 'Gemini 2.5 Flash — with thinking, better reasoning',
          gemini25Pro: 'Gemini 2.5 Pro — high-end, for complex questions',
          deepseekV4Flash: 'DeepSeek V4 Flash — cheap, fast',
          deepseekV4Pro: 'DeepSeek V4 Pro — powerful (75% off until 2026-05-05)',
          gpt4oMini: 'GPT-4o Mini — cheap, fast',
          gpt4o: 'GPT-4o — powerful, multimodal',
          claudeHaiku45: 'Claude Haiku 4.5 — cheap',
          claudeSonnet46: 'Claude Sonnet 4.6 — gold standard for many languages',
        },
        // ---- Booking integration ----
        sectionBooking: 'Hotel Booking Deep Linking',
        bookingMeta: 'Pick your hotel\'s booking engine. The bot sends the visitor there with dates and guests already filled in; the flow is added to the prompt automatically.',
        bookingEngineLabel: 'Booking engine',
        bookingUrlMetaCustomPage: 'The reservation page on your own site. The link is given as is, without dates.',
        bookingNotAppointments: 'For appointments (clinics, services) there are separate plugins: “Appointment links” (Calendly, Cal.com, Acuity) and “Google Calendar appointments”.',
        sectionBookingLinks: 'Appointment links',
        bookingLinksMeta: 'The bot gives the visitor a link to book an appointment in your system, with their name and email already filled in.',
        bookingLinksProviderLabel: 'Scheduling system',
        bookingLinksUrlLabel: 'Your event link',
        bookingLinksGoogleNote: 'Want the bot to see your free times and book straight into your calendar? That is the “Google Calendar appointments” plugin — we switch it on for you, just ask.',
        bookingUrlLabel: 'Booking URL',
        bookingUrlPlaceholder: 'https://reservations.example.com/yourhotel',
        bookingUrlMetaWebhotelier: 'Your reserve-online.net URL. The bot will append ?checkin=…&nights=…&rooms=…&adults=…&children=…&infants=… automatically.',
        bookingUrlMetaCosmicbooker: 'Your CosmicBooker URL. The bot will append ?checkin=…&checkout=…&guests=… automatically.',
        bookingUrlMetaCalendly: 'Full Calendly event URL (e.g. https://calendly.com/your-name/30min). Pre-fill name + email is added automatically.',
        bookingUrlMetaCalCom: 'Full Cal.com event URL (e.g. https://cal.com/your-name/30min). Pre-fill name + email + date is added automatically.',
        bookingUrlMetaAcuity: 'Your Acuity scheduling URL. Pre-fill first/last name + email + phone is added automatically.',
        bookingUrlMetaLodgify: 'Your Lodgify booking page URL. Arrival and departure dates plus guests are added automatically.',
        bookingGoogleCalendarMeta: 'Internal booking via Google Calendar — the bot handles slot search + booking inside the chat. Connect your team\'s Google Calendar in Team settings if you haven\'t already.',
        bookingNoneMeta: 'No booking flow. The bot will not push visitors towards a booking link.',
        bookingUrlMissingError: 'This engine requires a booking URL.',
        bookingSaveSuccess: 'Booking integration saved.',
        bookingSaveError: 'Could not save booking integration.',
        bookingEngines: {
          none: 'None',
          webhotelier: 'Web Hotelier (reserve-online.net)',
          cosmicbooker: 'CosmicBooker',
          google_calendar: 'Google Calendar (internal booking)',
          calendly: 'Calendly',
          cal_com: 'Cal.com',
          acuity: 'Acuity Scheduling',
          lodgify: 'Lodgify',
          custom_page: 'Your own reservation page',
        },
        sectionCatalog: 'Product catalogue (eShop)',
        catalogMeta: "Connect your e-shop's live catalogue so the bot answers live prices, stock and product links. The search tools are injected into the prompt automatically.",
        catalogSourceLabel: 'Catalogue source',
        catalogApiUrlLabel: 'API URL',
        catalogApiUrlPlaceholder: 'https://yourshop.gr/modules/vistochat/vistochat.php',
        catalogTokenLabel: 'Token',
        catalogTokenPlaceholder: 'vc_yourshop_…',
        catalogTokenKeep: 'Leave blank to keep the current token',
        catalogTokenIsSet: '✓ A token is saved (enter a new one to replace it)',
        sectionTicketing: 'Human escalation (support ticket)',
        ticketingMeta: 'Let the bot open a ticket in your helpdesk (Zendesk or a generic webhook) when a visitor needs a human — with a summary + the transcript.',
        ticketingLocked: 'Available on the Managed plan only — we set it up against your helpdesk.',
        ticketingEnable: 'Enable human-escalation tickets',
        ticketingProvider: 'Provider',
        ticketingWebhook: 'Generic webhook',
        ticketingSubdomain: 'Zendesk subdomain',
        ticketingAuthType: 'Zendesk authentication method',
        ticketingAuthBasic: 'API token (until 30 Apr 2027)',
        ticketingAuthOAuth: 'Connect with Zendesk (OAuth)',
        ticketingOAuthClientId: 'OAuth Client ID (Unique Identifier)',
        ticketingOAuthClientSecret: 'OAuth Client Secret',
        ticketingOAuthRedirectUri: 'Redirect URL (for Zendesk Admin Center)',
        ticketingOAuthConnectBtn: 'Connect with Zendesk (OAuth)',
        ticketingOAuthReconnectBtn: 'Reconnect with Zendesk (OAuth)',
        ticketingOAuthConnected: '✓ Connected to Zendesk via OAuth',
        ticketingOAuthNotConnected: 'OAuth connection not completed yet.',
        ticketingEmail: 'Agent email',
        ticketingToken: 'API token',
        ticketingWebhookUrl: 'Webhook URL',
        ticketingSecret: 'Signing secret',
        ticketingOptional: '(optional)',
        ticketingTags: 'Tags',
        ticketingKeep: 'Leave blank to keep the current value',
        ticketingCredsSet: '✓ Credentials saved (enter new ones to replace)',
        ticketingTest: 'Send test ticket',
        ticketTestOk: '✓ Test ticket created ({id})',
        ticketingHelp: 'The bot calls a create_ticket tool when a visitor asks for a human or has an issue it cannot resolve. Save first, then send a test ticket to verify.',
        catalogHelp: 'The token is set in your site module (vistoshop: $website_vistochat in domain.php). Read-only catalogue access.',
        catalogSources: {
          none: 'None',
          vistoshop: 'Vistoshop module (custom)',
          woo: 'WooCommerce',
        },
        sectionWebSearch: 'Web Search',
        webSearchMeta: 'Let the bot search the live web when the answer isn\'t in its knowledge/RAG. It cites the sources it used.',
        webSearchLocked: 'Web Search is available on the Max, Managed Site and Managed eShop plans. Upgrade this bot\'s plan to enable it.',
        webSearchEnableLabel: 'Enable web search',
        webSearchModeLabel: 'Sources',
        webSearchModes: {
          open: 'Whole web (bot decides)',
          allowlist: 'Preferred domains',
        },
        webSearchDomainsLabel: 'Preferred domains',
        webSearchDomainsPlaceholder: 'vistochat.com\nblog.vistochat.com',
        webSearchDomainsHelp: 'One domain per line. The bot is steered to prefer these sources and only cites them. Note: Google Search can\'t be hard-locked to a domain list, so treat this as a strong preference rather than a strict filter.',
        webSearchOpenHelp: 'The bot searches the whole web via Google Search and cites its sources. It prefers its own knowledge/RAG first.',
      },
      // ---- Widget tab ----
      widget: {
        sectionAppearance: 'Appearance',
        colorLabel: 'Color',
        positionLabel: 'Position',
        launcherStyleLabel: 'Launcher style',
        launcherStyles: {
          bubble: 'Bubble (classic)',
          pill: 'Pill (CTA)',
          preview: 'Preview (mini chat)',
        },
        themeLabel: 'Theme',
        themes: { light: 'Light', dark: 'Dark', auto: 'Auto (follow system)' },
        animationLabel: 'Open animation',
        animations: { none: 'None', slide: 'Slide-up', fade: 'Fade', bounce: 'Bounce' },
        autoOpenLabel: 'Auto-open delay (ms)',
        autoOpenPlaceholder: 'empty = never',
        autoOpenMeta: 'Auto-open the chat without click after N ms. Respects user-closed flag.',
        copyButtonLabel: 'Copy button on bot messages',
        teaserRotateLabel: 'Rotate balloon messages',
        teaserRotateMeta: 'Before the chat is opened, the balloon says in turn: welcome → review score → cheaper direct booking (whichever are active). One pass, stops on the last; every message opens the chat.',
        showBrandingLabel: 'Show "Powered by VistoChat" in footer',
        gdprConsentLabel: 'GDPR consent modal before first message',
        gdprConsentMeta: '⚠️ Blocking modal — visitor must accept before sending. Different from "compliance text" (passive disclaimer below the input). Set per-locale text in the Languages tab.',
        sectionAvatar: 'Avatar',
        avatarModePreset: 'Choose from gallery',
        avatarModeUpload: 'Upload image',
        avatarModeUrl: 'URL',
        avatarRemove: 'Remove',
        avatarPresetLoading: 'Loading…',
        avatarSavingPreset: 'Saving…',
        avatarUploadPlaceholder: 'Drag image or click to choose',
        avatarUploading: 'Uploading…',
        avatarUploadHint: 'PNG / JPG / WebP / GIF — up to 2MB. Recommend 256×256.',
        avatarUrlHint: 'HTTPS link to an image. Must be publicly reachable.',
        avatarErrorLoad: 'Image failed to load. Make sure the URL is HTTPS and reachable.',
        avatarErrorPresetsLoad: 'Could not load presets.',
        avatarErrorPresetSave: 'Could not save preset.',
        avatarErrorTooBig: 'File exceeds 2MB.',
        avatarErrorMime: 'Unsupported image format.',
        avatarErrorUpload: 'Upload failed.',
        avatarErrorClear: 'Could not remove avatar.',
        sectionCustomCss: 'Custom CSS',
        customCssMeta: 'Extra CSS applied inside the widget iframe. Use it to match your brand 100%.',
        customCssLockedMeta: 'Available on Max and Managed plans. Upgrade →',
        sectionCapabilities: 'Capabilities',
        capLeads: {
          label: 'Auto-extract leads',
          desc: 'Automatic detection of email/phone/name from the conversation; saved to /leads.',
        },
        capImageUpload: {
          label: 'Image upload',
          tagSoon: 'soon',
          desc: 'Visitor can upload an image and the AI analyzes it (Gemini multimodal).',
        },
        capFileUpload: {
          label: 'File upload',
          tagSoon: 'soon',
          desc: 'Upload PDF/DOCX/TXT — the AI can answer about the content.',
        },
        capVoiceInput: {
          label: 'Voice input',
          tagSoon: 'soon',
          desc: 'Voice input (mic) with transcription and optional audio responses.',
        },
        avatarCategoryLabels: {
          face: 'Real faces — also update the launcher',
          people: 'People',
          support: 'Customer Support',
          bot: 'Robots / AI',
          chat: 'Chat icons',
        },
      },
      // ---- Access tab ----
      access: {
        modeLabel: 'Who can talk to the bot',
        modes: {
          public: 'Public (everyone)',
          authenticated: 'Authenticated (with JWT)',
          mixed: 'Mixed (both)',
        },
        allowedOriginsLabel: 'URLs allowed to host the widget (one per line)',
        allowedOriginsMeta: 'Empty = allowed everywhere.',
      },
      // ---- Translations tab ----
      translations: {
        addLocaleLabel: 'New language',
        addLocalePlaceholder: '+ add…',
        addBtnAria: 'Add language',
        emptyHint: 'Pick a language above to view/edit its settings.',
        sectionUiTexts: 'Widget UI texts',
        botDisplayNameLabel: 'Bot name shown in the widget',
        startMessageLabel: 'Welcome message (start message)',
        teaserLabel: 'Teaser (above the launcher)',
        teaserPlaceholder: 'e.g. Need help?',
        teaserMeta: 'Empty = no teaser. The visitor can dismiss it — it won\'t reappear.',
        teaserDelayLabel: 'Display delay (ms)',
        complianceLabel: 'Compliance text (passive, below input)',
        compliancePlaceholder: 'e.g. Messages are processed by AI. Don\'t share sensitive personal data.',
        complianceMeta: 'Empty = hidden. Good for light GDPR disclosure.',
        gdprModalLabel: 'GDPR consent modal text (blocking, before first message)',
        gdprModalPlaceholder: 'Empty = default text in the visitor language. HTML allowed (e.g. links to your privacy policy).',
        gdprModalMeta: 'Shown only when the toggle in the Widget tab is on. HTML allowed — anchor tags are OK for privacy policy links.',
        sectionPromptOverride: 'System prompt — only for this language',
        promptOverrideLabel: 'Instructions for {locale}',
        promptOverridePlaceholder: 'If left empty, the bot\'s general prompt is used (Prompt & Model tab).',
        promptOverrideMeta: 'Empty = inherits from the bot-level prompt.',
        sectionKnowledgeOverride: 'Knowledge sources — only for this language',
        llmsTxtLabel: 'llms.txt URL',
        llmsTxtPlaceholder: 'https://example.com/llms.txt (empty = bot-level fallback)',
        sitemapLabel: 'sitemap.xml URL',
        sitemapPlaceholder: 'https://example.com/sitemap.xml (empty = bot-level fallback)',
        manualUrlsLabel: 'Manual URLs (one per line)',
        manualUrlsPlaceholder: 'Empty = bot-level fallback',
        chromaCollectionLabel: 'Chroma collection name (advanced)',
        chromaCollectionPlaceholder: 'bot_xxx (empty = auto-generated for the bot)',
        chromaCollectionMeta: 'Fill in only if you want this language to store chunks in a separate Chroma collection.',
        publishedLabel: 'Published',
        deleteLocaleBtn: 'Delete language',
        cancelBtn: 'Cancel',
        saveBtn: 'Save translation',
        savingBtn: 'Saving…',
        deleteToast: 'Translation {locale} deleted',
      },
      // ---- Knowledge (RAG) tab ----
      knowledge: {
        loadingState: 'Loading knowledge…',
        ragHeading: 'RAG for this bot',
        ragEnableLabel: 'RAG enabled (vector search on every question)',
        ragEnableMeta: 'On every question we run top-K retrieval from the knowledge base, filtered by the visitor language.',
        autoTitle: 'Automatic refresh',
        autoLabel: 'Re-read the sources every week',
        autoMeta: 'Once a week, overnight, the Brain re-reads the site and its other sources so it knows what changed. If the site does not respond or returns much less content than before, the previous knowledge is kept and our team is notified.',
        autoNext: 'Next refresh: around {date}',
        autoNeedsFirst: 'Starts after the first manual re-index.',
        ragHeading2: 'RAG tuning',
        ragTuneMeta: 'How many and how relevant chunks reach the AI. Default 5 results and cosine distance < 0.85.',
        topKLabel: 'Top-K (results per question)',
        topKMeta: 'How many chunks to ask Chroma for before applying min score. Range 1–20.',
        minScoreLabel: 'Min score (cosine distance)',
        minScoreMeta: 'Lower = more relevant chunks (stricter). Default 0.85.',
        searchModeLabel: 'Search mode',
        searchModes: {
          simple: 'Simple — only the latest message (default)',
          contextAware: 'Context-aware — includes conversation history (free)',
          smart: 'Smart Search — fast LLM rewrites the query',
          smartLockedSuffix: ' (Pro+)',
        },
        searchModeMeta: 'Simple: fast but loses context on follow-ups (e.g. "and how much is that?"). Context-aware: adds 2-3 previous user messages to the embedding query — negligible cost, better recall. Smart: uses a fast model to rewrite the question before vector search — dramatic improvement on ambiguous questions, +200 tokens/chat (~€0.00002).',
        saveRagBtn: 'Save RAG settings',
        saveRagBtnSaving: 'Saving…',
        fallbackHeading: 'Bot-level fallback sources',
        fallbackMeta: 'Used when a language has no sources of its own (in the Translations tab).',
        saveFallbackBtn: 'Save fallback sources',
        sectionStatus: 'Status per language',
        notReindexed: '· not yet reindexed',
        reindexAllBtn: '↻ Reindex all languages',
        sectionDocuments: 'Manual documents',
        documentsMeta: 'Upload files per language. Limit 25 MiB per file.',
        newUploadLegend: 'New upload',
        languageLabel: 'Language',
        fileLabel: 'File',
        uploadBtn: '↑ Upload',
        uploadingBtn: 'Uploading…',
        filterLegend: 'Filter',
        allLanguages: 'All',
        loadingDocs: 'Loading…',
        noDocsYet: 'No documents yet.',
        deleteDocBtn: 'Delete',
        docDeletedToast: 'Document "{filename}" deleted',
        errorTooLarge: 'File is too large (limit 25 MiB).',
        errorUnsupported: 'Only .pdf, .docx, .txt are allowed.',
        errorMimeMismatch: 'Content does not match the extension.',
        errorEmpty: 'Empty file',
        errorUploadGeneric: 'Upload error',
        errorLoadKnowledge: 'Error loading knowledge',
        errorLoadDocuments: 'Error loading documents',
      },
      // ---- Limits tab ----
      limits: {
        sectionBannedWords: 'Banned words',
        bannedWordsMeta: 'List of terms that, if they appear in the visitor message, block the conversation before it reaches the AI. One per line.',
        bannedWordsLabel: 'Banned words for this bot',
        sectionBannedIps: 'Banned IPs',
        bannedIpsMeta: 'IPs or CIDR ranges (e.g. {single} or {cidr}) that are blocked. Reads the {header} header.',
        bannedIpsLabel: 'Banned IPs for this bot',
        sectionDailyLimits: 'Daily limits',
        dailyLimitsMeta: 'Hard per-bot limits. 0 or empty = unlimited. Reset at UTC midnight.',
        queriesPerDayLabel: 'Queries per day',
        tokensPerDayLabel: 'Tokens per day',
        sectionInputLength: 'Per-message character cap',
        inputLengthMeta: 'Maximum length of the message a visitor can send. Protects against paste-spam that increases token cost without value. 0 = unlimited. Default 1000 characters (~250 tokens).',
        inputLengthLabel: 'Input max length (characters)',
        inputLengthHint: 'Server returns 400 with a friendly message when a visitor exceeds the limit. The widget enforces the cap on the textarea + counter.',
      },
      // ---- Embed tab ----
      embed: {
        sectionOptions: 'Embed options',
        optionsMeta: 'Override bot defaults only for this specific embed.',
        localeOverrideLabel: 'Language',
        positionOverrideLabel: 'Position',
        launcherOverrideLabel: 'Launcher style',
        colorOverrideLabel: 'Color override',
        sectionSnippet: 'Snippet to paste on the customer site',
        copyBtn: '📋 Copy',
        copiedBtn: '✓ Copied!',
        sectionWhere: 'Where it goes',
        whereLines: {
          paste: 'Paste it inside the <head> or right before </body> on every page.',
          wordpress: 'WordPress: a plugin like Insert Headers and Footers, or Yoast → Snippets, or theme functions.php using wp_footer.',
          shopify: 'Shopify: Theme → Edit code → theme.liquid before </body>.',
          dataLocale: 'data-locale is required only when more than 1 language is active.',
        },
        availableLocalesNote: 'Available: {locales}',
      },
      // ---- Plan & Billing tab ----
      plan: {
        sectionCurrent: 'Current plan',
        cycleYearly: 'yearly',
        cycleMonthly: 'monthly',
        trialUntil: 'Trial until {date}',
        nextChargeOn: 'Next charge: {date}',
        afterDowngrade: ' — then downgrades to Free.',
        managedNote: 'Manual billing: This bot is invoiced manually by Vistoweb (founding member or custom contract). Upgrade/cancel is handled by us.',
        capLanguages: 'Languages',
        capLanguagesValue: 'up to {n}',
        capConversations: 'Responses / month',
        capHardSuffix: ' (hard)',
        capUnlimited: 'unlimited ({n} fair-use)',
        cancelAtEndBtn: 'Cancel at period end',
        cancelling: 'Cancelling…',
        cancelConfirm: 'Cancel subscription for "{name}" at the end of the current period?',
        sectionUpgradeFree: 'Pick a plan for this bot',
        sectionUpgradeChange: 'Change plan',
        cycleAriaLabel: 'Billing cycle',
        cycleMonthlyBtn: 'Monthly',
        cycleYearlyBtn: 'Yearly',
        cycleSaveLabel: '−33%',
        priceMonthly: '/month',
        chargedYearly: 'Charged {amount}/year',
        managedBundleNote: 'Setup & onboarding included',
        capPerLang: 'Up to {n} languages',
        capWhiteLabel: 'White-label (no VistoChat branding)',
        capDiscreteBranding: 'Discreet branding',
        capUnlimitedConvs: 'Unlimited responses (fair-use)',
        capResponses: 'Up to {n} responses / month',
        capFreeSlots: '{n} free plugins (e.g. bookings)',
        capFreeSlotsAll: 'All free plugins',
        capPremiumSlots: '{n} premium plugins (Viber, Web Search, Vision…)',
        capCustomSlots: '{n} custom integrations by Vistoweb (e.g. live e-shop catalogue, Zendesk, WebHotelier availability)',
        currentPlanBtn: 'Current plan',
        choosePlanBtn: 'Choose',
        switchPlanBtn: 'Switch to this',
        openingCheckout: 'Opening checkout…',
        paddleNote: 'Payment via Paddle (PCI-DSS Level 1 — we never store card data). Enter VAT at checkout for B2B invoicing.',
        plans: {
          pro: { name: 'Pro', tagline: 'Serious AI assistant for one website.' },
          max: { name: 'Max', tagline: 'White-label + advanced analytics for one bot.' },
          elite: { name: 'Elite', tagline: 'More premium plugins and languages for demanding sites.' },
          managed: { name: 'Managed Site', tagline: 'Done-for-you for websites/services. Setup included.' },
        },
        errorCheckoutNoPaddle: 'Paddle client token not configured in .env',
        errorCheckoutNoPrice: 'This plan does not have a Paddle price_id configured yet. Try later or contact support.',
        errorCheckoutManaged: 'This bot is invoiced manually by Vistoweb.',
        errorCheckoutGeneric: 'Checkout error',
        partnerDiscountApplied: 'Partner Program: {percent}% off applied ({count} paid bots).',
        launchCouponApplied: '{code} applied: {percent}% off your first year! 🚀',
        errorCancel: 'Cancel error',
      },
    },
  },
  conversations: {
    feedback: {
      title: 'Notes & corrections',
      hint: 'If an answer was wrong, click "✎ Comment on this answer" right below it — that way we see exactly which one. We read every note and use it to improve your bot.',
      addNoteBtn: '+ Note on the whole conversation',
      correctBtn: '✎ Comment on this answer',
      correctHint: 'Tell us what was wrong with this answer and what it should have said',
      aboutLabel: 'About:',
      aboutWholeChat: 'The whole conversation',
      aboutAnswer: 'Answer at {time}: "{excerpt}"',
      linkedCount: '{n} comments on specific answers — shown under each one (⚑).',
      flaggedHint: 'You reported this answer',
      kindNote: 'Note',
      kindCorrection: 'Correction',
      bodyPlaceholderNote: 'What should we know about this conversation?',
      bodyPlaceholderCorrection: 'What was wrong with this answer?',
      expectedPlaceholder: 'What should it have answered? (optional, but this is what helps most)',
      expectedLabel: 'Should have said:',
      empty: 'No notes on this conversation yet.',
      saveError: 'Could not save. Please try again.',
      confirmDelete: 'Delete this note?',
      status: {
        open: 'sent',
        reviewed: 'seen',
        applied: 'applied',
        dismissed: 'closed',
      },
    },
    heading: 'Conversations',
    subtitle: '{total} chats from {bots} bots — click to open the full transcript.',
    filters: {
      allBots: 'All bots',
      allChannels: 'All channels',
      channelWeb: 'Web chat',
      channelEmail: 'Email',
      channelViber: 'Viber',
      allLeadStates: 'All',
      withLead: 'With lead',
      withoutLead: 'Without lead',
      searchPlaceholder: 'Search inside messages…',
    },
    empty: {
      heading: 'No conversations',
      body: "Visitor conversations show up here. If you just created a bot, paste the embed code into your site.",
      cta: 'Manage bots',
    },
    table: {
      bot: 'Bot',
      started: 'Started',
      lastActivity: 'Last activity',
      messages: 'Messages',
      tokens: 'Tokens',
      lead: 'Lead',
      lastUserQuestion: 'Last user question',
      noIp: 'no-ip',
    },
    pager: {
      prev: '‹ Prev',
      next: 'Next ›',
      info: 'Page {page} / {pages} · {total} total',
    },
    drawer: {
      ariaLabel: 'Conversation',
      messageCount: '{n} messages',
      loadingMessages: 'Loading messages…',
      titleError: 'Error',
      pageLabel: 'Page',
      referrerLabel: 'Referrer',
      uaLabel: 'UA',
      leadLabel: 'Lead',
    },
    roles: {
      visitor: 'Visitor',
      bot: 'Bot',
      agent: 'Team (human)',
    },
    takeover: {
      liveBadge: 'Live',
      botHasIt: 'The bot is answering this conversation.',
      takeBtn: 'Take over',
      releaseBtn: 'Return to bot',
      youHaveIt: 'You have the conversation — the bot is paused.',
      someoneHasIt: 'Another team member has the conversation.',
      replyPlaceholder: 'Type your reply to the visitor… (Enter to send)',
      sendBtn: 'Send',
      suggestBtn: '✨ AI suggestion',
      suggestHint: 'The AI drafts a suggested reply from your bot\'s knowledge — edit it before sending.',
    },
  },
  leads: {
    heading: 'Leads',
    subtitle: '{total} leads from {bots} bots — click a row to edit.',
    filters: {
      allBots: 'All bots',
      allStatuses: 'All statuses',
      searchPlaceholder: 'Email, phone, name or company…',
    },
    empty: {
      heading: 'No leads',
      body: 'When visitors leave their email or phone in the conversation, they appear here. Make sure the widget is installed on a live site.',
      cta: 'Manage bots',
    },
    table: {
      bot: 'Bot',
      contact: 'Contact',
      captured: 'Captured',
      status: 'Status',
      followup: 'Followup',
      chat: 'Chat',
      noContact: '(no contact info)',
      msgsShort: '{n} msgs',
    },
    drawer: {
      ariaLabel: 'Lead',
      sectionRequest: 'Request (as confirmed in the chat)',
      sectionContact: 'Contact details',
      labelName: 'Name',
      labelEmail: 'Email',
      labelPhone: 'Phone',
      labelCompany: 'Company',
      labelLocale: 'Locale',
      labelPage: 'Page',
      labelReferrer: 'Referrer',
      sectionStatus: 'Status & notes',
      statusLabel: 'Status',
      statusNoteLabel: 'Status note (optional)',
      notesLabel: 'Internal notes',
      followupDateLabel: 'Followup date',
      followupStatusLabel: 'Followup status',
      followupNone: '—',
      saveBtn: 'Save changes',
      saving: 'Saving…',
      sectionChat: 'Conversation ({n} messages)',
      chatUnavailable: 'The conversation is not available.',
      loadingChat: 'Loading…',
    },
    fallback: {
      leadId: 'Lead #{id}',
    },
    // Pill labels for `leads.status` (canonical English values stored in DB:
    // new/valid/test/duplicate/contacted/qualified/won/lost/spam). The
    // `services/leads.ts:leadStatusLabel(status)` helper looks these up at
    // render time so dashboard pills follow the active locale.
    statusLabels: {
      new: 'New',
      valid: 'Valid',
      test: 'Test',
      duplicate: 'Duplicate',
      contacted: 'Contacted',
      qualified: 'Qualified',
      won: 'Won',
      lost: 'Lost',
      spam: 'Spam',
    },
  },
  knowledge: {
    heading: 'Knowledge (RAG)',
    subtitle: '{enabled}/{total} bots active · {chunks} chunks total',
    reindexAllBtn: '↻ Reindex all',
    viewCards: 'Cards',
    viewList: 'List',
    cols: { bot: 'Bot', rag: 'RAG', chunks: 'Chunks', locales: 'Languages', lastReindex: 'Last reindex' },
    loadFailed: 'Loading error.',
    notLoaded: 'Failed to load.',
    statusOn: 'RAG on',
    statusOff: 'RAG off',
    cardSettings: '⚙ Settings',
    statChunks: 'Chunks',
    statCollection: 'Collection',
    statLastReindex: 'Last reindex',
    neverReindexed: 'never',
    reindexBtn: '↻ Reindex all',
    reindexing: 'Indexing…',
    sourcesLocale: 'locale sources',
    sourcesFallback: 'fallback sources',
    sourcesNone: 'no sources',
    errorNoSources: '{name}: has no configured sources. Open Settings → Knowledge.',
    errorReindexFailed: 'reindex failed',
  },
  team: {
    heading: 'Team · {name}',
    fallbackName: 'Team',
    subtitle: 'Manage members and invitations.',
    sectionMembers: 'Members',
    yourRolePrefix: 'Your role:',
    emptyMembers: 'No members.',
    sectionNewInvite: 'New invitation',
    emailLabel: 'Email',
    roleLabel: 'Role',
    onlyOwnerSuffix: '(owner only)',
    submitInvite: 'Create invitation',
    submittingInvite: 'Creating…',
    inviteEmailUsed: 'This email is already a member of the team.',
    sectionPending: 'Pending invitations',
    expiresPrefix: 'expires',
    invitedByPrefix: 'from',
    copyInviteBtn: '📋 Copy link',
    revokeInviteBtn: 'Cancel',
    confirmRevoke: 'Cancel invitation for {email}?',
    inviteCreatedToast: 'Invitation created. Copy the link and send it to the recipient.',
    inviteCopiedToast: 'Link copied for {email}.',
    inviteCopyFailed: 'Could not copy — please copy manually.',
    inviteCancelError: 'Could not cancel the invitation.',
    inviteCreateError: 'Could not create invitation.',
    roleChangeError: 'Could not change role.',
    roleChangedToast: '{email} is now {role}.',
    confirmLeave: 'Leave this team?',
    leftTeamToast: 'You left the team.',
    confirmTransfer: 'Transfer ownership to {email}? You become an admin afterwards.',
    transferDoneToast: 'Ownership transferred to {email}.',
    transferFailed: 'Transfer failed.',
    selfLabelLeave: 'Leave',
    memberRemovedToast: '{email} removed from the team',
    inviteEmailNotSentNote: 'Email delivery is not active yet — share the link below manually.',
    joinedPrefix: 'Joined',
    lastLoginPrefix: 'last login',
    transferAriaTitle: 'Transfer ownership',
    transferBtn: '→ Owner',
    removeBtn: 'Remove',
    invitableRoles: {
      owner: 'Owner',
      // See the note on the Greek copy: "Admin" collided with the Vistoweb
      // platform admin (users.is_admin), which is never granted from this screen.
      // Label only — the role and its permissions are unchanged.
      admin: 'Team admin',
      editor: 'Editor',
      agent: 'Agent',
      member: 'Member',
      viewer: 'Viewer',
    },
    roleDescriptions: {
      admin:  'Full management WITHIN this team (except transfer of ownership) — adds/removes members, manages bots & subscription. Always below the Owner.',
      editor: 'Can edit bots, knowledge, conversations.',
      agent:  'Manages leads & conversations but cannot change bot settings.',
      member: 'Read+chat: sees conversations & leads but cannot modify them.',
      viewer: 'Read-only access to all team data.',
    },
  },
  billing: {
    platformRoot: {
      title: 'Vistoweb is invoiced by no one',
      body: 'This page shows a customer or partner what we charge them. For Vistoweb, at the top, the amounts of the companies below are REVENUE — per bot in Managed subs, in total (MRR, cost, margin) in System stats, and partners\' price lists in Companies.',
      managed: 'Managed subs',
      stats: 'System stats',
      companies: 'Companies',
    },
    heading: 'Subscription',
    subtitle: 'Plans, charges and invoices. Each chatbot has its own subscription — you upgrade or cancel separately from its page.',
    sectionSummary: 'Account total',
    badgeSandbox: 'SANDBOX',
    cellMonthlyTotal: 'Monthly total',
    perMonth: '/month',
    extManualLine: '+ {n} bot with manual billing | + {n} bots with manual billing',
    cellBots: 'Bots',
    free: '{n} free',
    paid: '{n} paid',
    cellPartner: 'Partner Program',
    customDiscount: 'Custom',
    customDiscountNote: '50+ bots — contact us',
    portalBtn: 'Manage via Paddle (portal)',
    portalLoading: 'Opening…',
    sectionInvoiceDetails: 'Billing details',
    savedToast: 'Saved ✓',
    invoiceIntro: 'If your business is B2B with a valid VAT, fill it in so checkout applies reverse-charge automatically and no local VAT is charged. We save your details so you don\'t need to retype them on every upgrade.',
    companyNameLabel: 'Company name',
    companyNamePlaceholder: 'e.g. Promowave Ltd',
    billingEmailLabel: 'Billing email',
    billingEmailHint: '(optional — the email address where invoices are sent)',
    billingEmailPlaceholder: "billing{'@'}yourcompany.com",
    vatLabel: 'VAT / Tax ID',
    vatPlaceholder: 'e.g. EL801286009 or GB293075488',
    countryLabel: 'Country',
    countryHint: '(ISO 2-letter)',
    countryPlaceholder: 'GR',
    saveBtn: 'Save',
    savedBtn: 'Saved',
    saving: 'Saving…',
    reverseChargeNote: '✓ Next checkout will apply B2B reverse-charge ({country} {vat}).',
    errorInvalidCountry: 'Provide a 2-letter ISO country code (e.g. GR, GB, DE).',
    errorInvalidVat: 'VAT can only contain letters/digits/spaces/dots/hyphens.',
    errorPortal: 'Portal error',
    reseller: {
      title: 'Bots you are invoiced for',
      intro: 'Vistoweb invoices you for every bot below — your own company and your {customers} customers, {bots} in total. Each amount is the agreed price for that bot, not a list price.',
      ownCompany: 'your own company',
      countMeta: 'across {companies} companies, yours included',
      unpriced: 'no price set',
      noBots: 'no active bot yet',
      unpricedNote: '{n} bot(s) have no agreed price yet, so they are not in the total. Ask us to set them.',
    },
    sectionBots: 'Bots',
    botsEmpty: 'No bots yet.',
    firstBotLink: 'Create your first',
    monthShort: '/month',
    annualLabel: '· annual',
    cancelsAt: 'cancels {date}',
    sectionPlansHeading: 'Plans (per chatbot)',
    managedPanel: {
      badge: 'Founding Member',
      heading: 'Fully managed by Vistoweb',
      sub: 'We set it up, train it and manage it for you — no plan to choose, nothing to configure.',
      save: 'you save {amount}/month ({pct}%)',
      foot: 'Invoiced directly by Vistoweb E.E. (Greek invoice). To change your package, just get in touch.',
    },
    cycleAriaLabel: 'Billing cycle',
    cycleMonthly: 'Monthly',
    cycleYearly: 'Yearly',
    cycleSave: '−33%',
    plansIntro: 'Prices are per chatbot, not per account. Create a separate bot for each audience (guest support, internal staff, subscribers) and you only pay for the ones you activate.',
    feature1lang: 'Up to {n} language',
    featureNlang: 'Up to {n} languages',
    featureFreeMessages: '{n} responses/month',
    featureFreeBranding: 'VistoChat branding on widget',
    featureFreeBotsCap: 'Up to 3 Free bots per account',
    featureUnlimitedFair: 'Unlimited responses (fair-use {n}/month soft cap)',
    featureWhiteLabel: 'White-label widget',
    featureDiscreetBranding: 'Discreet VistoChat branding',
    featureApiWebhooks: 'API & webhooks',
    featureAdvancedAnalytics: 'Advanced analytics ({n} days history)',
    featureBasicAnalytics: 'Basic dashboard (30 days)',
    featureCsvExport: 'CSV export of stats',
    featureTopQuestions: 'Top user questions (auto-clustering)',
    featurePrioritySupport: 'Priority support',
    featureManagedService: 'Done-for-you setup + monthly tuning',
    featureFreeTrial: '{n}-day free trial',
    setupOneTime: '+ {amount} one-time setup',
    chargedYearly: 'Charged {amount} / year',
    perPeriodMonthly: '— per month —',
    upgradeFromBotPage: 'Upgrade individual bots from each bot\'s page.',
    fairUseNote: 'Fair-use: Unlimited responses for normal business use. Bot/scraping/spam traffic is not covered. Very high volume (>5,000/month per bot) may need a custom plan.',
    sectionInvoices: 'Invoices',
    invoicesEmpty: 'No invoices yet.',
  },
  stats: {
    heading: 'Stats',
    subtitle: 'Per-bot analytics — on every plan.',
    botListEmpty: 'You don\'t have any bots in your team yet.',
    newBotCta: '+ New bot',
    upgradeHeading: 'Unlock advanced analytics',
    upgradeBody: 'Free shows 30 days of history. Every paid plan extends that to 365 days and adds your top user questions.',
    upgradeFeatures: {
      daily: 'Conversations, leads, messages per day',
      conversion: 'Conversion rate & avg response time',
      heatmap: 'Hour × day heatmap to see when your peak traffic is',
      csv: 'CSV export for your CRM/spreadsheet',
    },
    seePlansCta: 'See plans →',
    botsOnFreePro: 'Bots on Free/Pro: {names}',
    controls: {
      botLabel: 'Bot',
      botGroupCombined: 'Aggregated',
      botAllOption: 'All bots ({n} aggregated)',
      botAllLabel: 'All bots',
      botGroupAnalytics: 'With analytics',
      botGroupLocked: 'Free / Pro (upgrade required)',
      botLockedSuffix: '— upgrade',
      rangeLabel: 'Range',
      downloadCsv: 'Download CSV',
      downloading: 'Downloading…',
    },
    statsCards: {
      conversations: 'Conversations',
      leads: 'Leads',
      conversion: 'Conversion',
      messages: 'Messages',
      avgResponse: 'Avg response',
      bookingClicks: 'Booking clicks',
      bookingClicksMeta: '{n} total link clicks',
      bookingClicksEst: '~{e} historical estimate* · {m} measured',
      bookingClicksEstTooltip: '*Estimate: 85% of the {base} conversations where the bot proposed a booking link before tracking began (12 Jun 2026). Measured clicks are exact from that date on.',
    },
    historyClamped: 'Your history is capped at {days} days (plan {plan}).',
    chartChats: 'Conversations',
    chartChatsLeadsTitle: 'Conversations & Leads per day',
    chartLeads: 'Leads',
    chartUserMessages: 'User messages',
    chartAiMessages: 'AI replies',
    chartMessagesTitle: 'Messages per day',
    sectionFunnel: 'Lead funnel',
    funnelMeta: '{leads} leads · {pct}% conversion',
    funnelEmpty: 'No leads in this range.',
    sectionTopPages: 'Top entry pages',
    topPagesMeta: 'where conversations start from',
    topPagesEmpty: 'No page URLs captured (needs Content Aware or newer chats).',
    sectionTopics: 'Top user questions',
    topicsMeta: 'week of {week} · {n} visitor messages analysed',
    topicsEmpty: 'Nothing to group yet. Themes appear once a bot receives about 15 visitor messages in a week, and are refreshed every Monday.',
    sectionHeatmap: 'Traffic by hour/day',
    heatmapMeta: 'server hours (UTC) · max {n} chats',
    heatmapTooltip: '{day} {hour}:00 — {n} chats',
    errorLoadBots: 'Could not load the bot list.',
    errorBotPlanLocked: 'This bot does not include advanced analytics.',
    errorLoad: 'Loading error.',
    errorCsv: 'Could not download the CSV.',
  },
  account: {
    heading: 'Account',
    subtitle: 'Your details + data rights (GDPR).',
    sectionProfile: 'Account details',
    labelName: 'Name',
    labelEmail: 'Email',
    pillUnverified: 'unverified',
    labelLogin: 'Sign-in',
    loginSso: 'SSO ({provider})',
    loginPassword: 'Email + password',
    labelTeam: 'Current team',
    labelTermsAccepted: 'Terms accepted',
    sectionPassword: 'Change password',
    passwordBody: 'You will stay signed in here; every other device is signed out. We email you a notice.',
    pwCurrent: 'Current password',
    pwNew: 'New password',
    pwConfirm: 'Repeat new password',
    pwHint: 'At least 8 characters.',
    pwMismatch: 'The two new passwords do not match.',
    pwTooShort: 'The new password must have at least 8 characters.',
    pwBtn: 'Change password',
    pwSaving: 'Saving…',
    pwDone: 'Your password was changed. Other devices were signed out.',
    pwError: 'Could not change the password.',
    pwSso: 'You sign in with {provider} — there is no password to change here.',
    sectionExport: 'Data export',
    exportBody: 'Download a JSON file with all your account data: profile, teams, bots you own, conversations and leads. Per Articles 15 + 20 GDPR.',
    exportBtn: 'Download JSON',
    exporting: 'Generating file…',
    errorExport: 'Could not export data',
    sectionDelete: 'Delete account',
    deleteBody: 'Deletion is permanent and irreversible. We remove all your personal data from our systems per Article 17 GDPR. If you own a team with active bots, you\'ll be asked either to transfer ownership or to confirm deletion of those teams together with your account.',
    deleteOpenBtn: 'Delete account…',
    deleteModalTitle: 'Confirm deletion',
    deleteModalBody: 'After {email} is deleted, you will not be able to sign in again with these credentials. All personal data is permanently removed.',
    deleteBlocker: {
      heading1: 'You own {n} team with active bots:',
      headingN: 'You own {n} teams with active bots:',
      line: '{name} — {bots} bots',
      pickPrompt: 'Choose:',
      transferLink: 'Transfer ownership →',
      transferLinkSuffix: '(must be done per team)',
      orWipe: 'or check below and the deletion will wipe all of them.',
      acknowledge: 'I agree to delete my teams + bots ({n})',
    },
    passwordLabel: 'Password',
    typeDeleteLabel: 'Type {token} to confirm',
    cancelBtn: 'Cancel',
    deleteBtn: 'Delete account',
    deleting: 'Deleting…',
    errorDelete: 'Deletion error',
    closeAria: 'Close',
  },
  settings: {
    heading: 'System settings',
    subtitle: 'Manage master API keys and global parameters. Super admins only.',
    saveAllBtn: 'Save all',
    saving: 'Saving…',
    noChanges: 'No changes.',
    savedToast: 'Saved.',
    groups: {
      ai: 'AI Providers',
      chroma: 'Chroma Vector DB',
      ops: 'Operational guardrails',
      billing: 'Billing (Paddle)',
    },
    pillSecret: 'secret',
    placeholderSecretWithValue: '{value} (type new value to change)',
    placeholderUnsetSecret: 'No key set',
    placeholderUnset: 'No value set',
    revertBtn: '↺ env',
    revertTitle: 'Revert to .env value',
    infoCard1: {
      title: '📌 How values work',
      lines: {
        env: 'env: value comes from the .env file on the server. Safest option.',
        database: 'database: you set an override via this page. Takes priority over .env.',
        unset: 'unset: not configured anywhere. Related services won\'t work.',
        revert: 'To clear a database override and fall back to .env, click ↺ env.',
        secrets: 'For secrets, the displayed value is masked (first/last 4 chars). For the full value, see .env via SSH.',
      },
    },
    infoCard2: {
      title: '🔑 BYO API keys (for customers)',
      intro: 'When a customer brings their own OpenAI/Gemini/Chroma key (BYO):',
      lines: {
        schema: 'Schema ready: bots.custom_api_key_enc + bots.custom_chroma_key_enc (AES-256-GCM encrypted in DB)',
        ui: 'UI lands in BotsView Settings → Prompt & Model tab once the multi-provider framework is built (Backlog #8)',
        when: 'Activate when the first enterprise request comes in — not worth coding without a real use case',
      },
    },
  },


  customPlugin: {
    breadcrumb: 'Custom plugin',
    catalogTitle: "This Brain's custom plugins",
    catalogIntro: 'Features we built specifically for you. They only work on this Brain and are switched on by the Vistoweb team.',
    madeFor: 'Built specifically for this Brain by Vistoweb.',
    notFound: 'This plugin does not exist on this Brain.',
    status: { active: 'Active', off: 'Off', overLimit: 'Over plan limit' },
    lastRun: 'Last check',
    toolNote: 'Works inside conversations: the Brain uses it when needed.',
    history: 'History',
    sentThisMonth: 'Replies this month: {n}',
    noEvents: 'Nothing recorded yet.',
    col: { date: 'Date', status: 'Status', recipient: 'Recipient', subject: 'Subject' },
    ev: { sent: 'Sent', skipped: 'Skipped', failed: 'Failed' },
    reason: {
      sender: 'other sender', 'already-replied': 'already replied', 'no-reply-to': 'no requester address',
      'bad-recipient': 'invalid recipient', 'auto-submitted': 'automatic message', precedence: 'bulk mail', spam: 'spam',
      'send-error': 'send error — will retry', 'gave-up': 'send error — gave up',
    },
    badJson: 'The settings are not valid JSON.',
    saved: 'Saved.',
    admin: {
      title: 'Settings (Vistoweb only)',
      slots: 'Custom slots on the plan: {used}/{limit}',
      enable: 'Active',
      config: 'Settings (JSON)',
      secretKey: 'Secret — name',
      secretValue: 'Secret — value (write-only)',
      secretKeep: 'Stored — leave blank to keep',
      tests: 'Tests',
      testConn: 'Test connection',
      dryRun: 'Dry run (no sending)',
      runNow: 'Run now',
      testTo: 'Send a test reply to (no Cc to the customer)',
      sendTest: 'Send test',
      toolInput: 'Tool input (JSON)',
      runTool: 'Test tool',
      lastError: 'Last error',
    },
  },
  brain: {
    soon: 'Soon',
    nav: {
      aria: 'Brain menu',
      allBrains: 'All Brains',
      switch: 'Switch Brain',
      brain: 'Brain',
      channels: 'Channels',
      skills: 'Skills',
      activity: 'Activity',
      manage: 'Manage',
      website: 'Website agent',
      viber: 'Viber agent',
      email: 'Email agent',
    },
    views: {
      overview: 'Overview',
      knowledge: 'Knowledge',
      instructions: 'Instructions',
      languages: 'Languages',
      settings: 'Settings',
      plan: 'Plan & Billing',
      channels: 'Channels',
      skillsCatalog: 'Skills',
      viberPage: 'Viber',
      emailPage: 'Email',
      website: {
        instructions: 'Instructions',
        appearance: 'Appearance',
        texts: 'Texts',
        access: 'Access & domains',
        embed: 'Embed code',
        test: 'Test',
      },
      skills: {
        booking: 'Hotel Booking Deep Linking',
        bookinglinks: 'Appointment links',
        catalog: 'Product catalogue',
        ordertracking: 'Order tracking',
        reviews: 'Reviews',
        prices: 'Price comparison',
        leads: 'Leads Capture',
        ticketing: 'Ticketing',
        websearch: 'Web Search',
      },
    },
    prices: {
      intro: 'Two things: a balloon next to the chat button ("Book direct: usually 15%+ cheaper than Booking.com" + an invitation to tell the assistant their dates in the chat; the booking link is given inside the conversation), and a comparison for specific dates when a visitor asks a price in the chat.',
      honesty: 'Fixed rules: compared only with the OTAs you choose; prices are shown only when the direct price is cheaper than all of them; the balloon percentage appears only if it held in EVERY sample (at least 3) and is rounded down.',
      label: 'Price comparison on',
      needsEngine: 'Needs a booking engine link in Hotel Booking Deep Linking.',
      needsBooking: 'Needs Hotel Booking Deep Linking switched on (the assistant hands over the booking engine link inside the chat).',
      settingsTitle: 'Settings',
      hotelId: 'Google Hotels identifier',
      hotelIdFromReviews: 'Empty = the one from Reviews is used.',
      hotelIdHelp: 'Find it with the hotel search on the Reviews page.',
      otas: 'Compare with',
      otasHelp: 'Big OTAs only. Small aggregators often undercut and are not compared.',
      showBalloon: 'Show the balloon next to the chat button',
      liveCompare: 'Compare for the visitor\'s dates inside the chat',
      cap: 'Comparisons per day',
      capHelp: 'Each new comparison costs ~$0.004 (the same dates are kept for 6 hours).',
      saved: 'Saved.',
      measureTitle: 'Weekly measurement',
      measureHelp: 'Every Monday 8 future 2-night stays (6 midweek, 2 weekends) are checked on Google Hotels.',
      headline: 'The balloon says: "usually {pct}%+ cheaper than {ota}"',
      claimStale: 'The last measurement is older than {days} days — the percentage is NOT shown in the balloon or said by the assistant until a new measurement succeeds.',
      noHeadline: 'No safe percentage from the measurement — the balloon is not shown.',
      sampleInfo: '{n} of {total} dates had prices · measured {date}',
      colPairs: 'Samples', colRange: 'Cheaper direct', colClaim: 'Claim',
      claimYes: 'usually {pct}%+', claimNo: '— (too few samples or not always cheaper)',
      samplesTitle: 'Prices per date (per night)',
      colDates: 'Dates', colDirect: 'Direct',
      samplesNote: 'From Google Hotels; indicative. Empty = no prices for those dates.',
      noSample: 'No measurement yet.',
      sampleNow: 'Measure now',
      samplingShort: 'Measuring…',
      sampling: 'Measurement started; it takes up to an hour. This page will update by itself.',
      sampled: 'Measurement finished.',
      schedule: 'Repeated automatically every Monday.',
      lastError: 'The last measurement failed ({date}) — the previous one applies:',
      previewTitle: 'What visitors see',
      noPreview: 'No balloon is shown (no safe percentage or no booking engine link).',
      chatPreview: 'In the chat, when asked a price with dates: "Direct €149 a night — €184 on Booking.com" (indicative prices from Google Hotels) + the booking link. If an OTA is cheaper for those dates, or there are no prices, no price is given — only the link.',
    },
    reviews: {
      intro: 'Your rating appears in the chat; one click shows visitors the sources, what customers mention and the latest reviews. The Brain answers with the same numbers when asked about quality.',
      honesty: 'All reviews are shown, newest first, each linked to its source — none are picked or hidden, as EU consumer law requires.',
      label: 'Reviews on',
      noDataYet: 'On, but no reviews loaded yet — set the source and press "Refresh now". Nothing is shown until then.',
      sourceTitle: 'Where they come from',
      googleQuery: 'Your business on Google',
      googleQueryPh: 'e.g. Zagori Suites Vitsa',
      googleQueryHelp: 'Name and area as someone would search it on Maps. After the first refresh, check in the preview that the right business was found.',
      hotelId: 'Google Hotels identifier (hotels only)',
      hotelIdHelp: 'Adds scores per topic (breakfast, cleanliness…) and other sites such as Tripadvisor. Find it with the search below.',
      findHotel: 'Find your hotel',
      findHotelPh: 'Hotel name',
      search: 'Search',
      noCandidates: 'No hotel found with that name.',
      manualTitle: 'Scores you state yourself',
      manualHelp: 'Booking and Expedia do not provide their scores automatically. Enter them as shown on your page there, with the link to that page; visitors see them alongside the others. Keep them up to date — they are not refreshed automatically.',
      platform: 'Platform', other: 'Other', name: 'Name', score: 'Score', outOf: 'Out of', count: 'Reviews', url: 'Link',
      addManual: 'Add a score',
      statedOn: 'Stated on {date}',
      refreshNow: 'Refresh now',
      refreshingShort: 'Refreshing…',
      refreshing: 'Refresh started; it takes 1-3 minutes. This page will update by itself.',
      refreshed: 'Reviews refreshed.',
      saved: 'Saved.',
      schedule: 'Refreshed automatically once a week.',
      previewTitle: 'What visitors see',
      placeCheck: 'Make sure this is your business.',
      reviewsWord: 'reviews',
      stated: 'stated by the business',
      previewCounts: '{cats} topics · {reviews} latest reviews',
      updated: 'Updated',
      lastError: 'The last refresh failed ({date}) — the previous data is shown:',
      noSnapshot: 'No reviews loaded yet.',
      showTitle: 'Which reviews are shown',
      showLabel: 'Review list in the chat',
      showAll: 'All (newest first)',
      show4: '4★ and above only',
      show5: '5★ only',
      showHelp: 'With a filter, the list is shown as "Selected reviews" with a link to all reviews on Google, and the overall rating stays the real one (it includes every review). Consumer-protection law requires this and it cannot be changed.',
      showCount: 'With this choice {n} reviews are shown.',
    },
    orderTracking: {
      intro: 'When a customer asks about an order, the Brain asks for the order number and the email or phone used on it, and fetches the live status, payment and shipping from your eShop.',
      privacy: 'If the number does not match the email or phone, nothing about the order is revealed.',
      needsCatalog: 'Needs the eShop catalogue active, because it uses the same connection to your shop.',
      label: 'Order tracking on',
    },
    overview: { agentsTitle: 'Agents on this Brain', manage: 'Manage', noneActive: 'None active yet' },
    agentInstr: {
      title: '{agent} — own instructions',
      introChannel: 'Added on top of the Brain\'s central instructions, only in conversations on {agent}. They do not replace the central ones: anything that should hold on every channel belongs there, and if the two disagree, change the central instructions.',
      introSkill: 'Added on top of the Brain\'s central instructions, only while {agent} is in use. They do not replace the central ones: anything that should always hold belongs there.',
      inactive: 'This skill is off, so these instructions are kept but not used until you turn it on.',
      save: 'Save instructions',
      saving: 'Saving…',
      saved: 'Saved — applies from the next message.',
      example: {
        website: 'e.g. When the visitor is on the prices page, suggest booking directly for the best rate.',
        viber: 'e.g. Reply in 1–2 sentences. At the end, ask whether they would like us to call them.',
        email: 'e.g. Sign every reply as "The reservations team".',
        whatsapp: 'e.g. Keep replies short; send one link at a time.',
        leads: 'e.g. Before the conversation ends, ask for a phone number so we can follow up.',
        booking: 'e.g. Always mention that booking directly on our site is 10% cheaper.',
        catalog: 'e.g. Always suggest one matching accessory alongside the product asked for.',
        ordertracking: 'e.g. If the order has not shipped after 3 working days, give the shop\'s phone number.',
        reviews: 'e.g. When asked about breakfast, say what guests write about it.',
        prices: 'e.g. When comparing prices, also mention that booking direct includes free cancellation.',
        ticketing: 'e.g. Open a ticket only once you have the customer\'s email.',
        websearch: 'e.g. Use the web only for weather and local events.',
      },
    },
    catalog: {
      add: 'Add',
      addChannel: 'Add a channel',
      addSkill: 'Add a skill',
      search: 'Search…',
      noResults: 'Nothing matches your search.',
      channelsTitle: 'Channels',
      channelsIntro: 'Where your Brain talks to people. Every channel uses the same knowledge, instructions and skills — you train it once.',
      skillsTitle: 'Skills',
      skillsIntro: 'What your Brain can do beyond answering: capture leads, take bookings, search your shop, hand over to your team.',
      status: {
        active: 'Active',
        included: 'Included in your plan',
        upgrade: 'From {plan}',
        soon: 'Coming soon',
        onRequest: 'On request',
        full: 'No free slot',
        requires: 'Needs: {agent}',
      },
      action: {
        configure: 'Settings',
        enable: 'Turn on',
        upgrade: 'Upgrade plan',
        request: 'Request setup',
        disable: 'Turn off',
      },
      requestSubject: 'Connect {agent} to {brain}',
      slots: {
        free: 'Free plugins: {used} of {limit}',
        freeAll: 'Free plugins: {used} (all included)',
        premium: 'Premium plugins: {used} of {limit}',
        custom: 'Custom: {used} of {limit}',
      },
      overLimit: 'Beyond your plan — not working',
    },
    agents: {
      website: { title: 'Website', desc: 'The chat on your website. Answers your visitors 24/7 from the Brain\'s knowledge, in their language.' },
      viber: { title: 'Viber', desc: 'Customers message you on Viber and the same Brain replies — same knowledge, same rules. Conversations appear alongside all the others.' },
      email: { title: 'Email', desc: 'Answers incoming email to your shared inbox, or prepares a draft for you to approve.' },
      whatsapp: { title: 'WhatsApp', desc: 'Conversations through WhatsApp Business, answered by the same Brain.' },
      leads: { title: 'Leads Capture', desc: 'Spots a name, email or phone number in the conversation and notifies you instantly, so no interested customer slips away.' },
      booking: { title: 'Hotel Booking Deep Linking', desc: 'Sends the visitor into your hotel\'s booking engine with dates and guests already filled in. Supports WebHotelier, CosmicBooker, Lodgify and your own reservation page.' },
      bookinglinks: { title: 'Appointment links', desc: 'Gives the visitor a link to book an appointment in Calendly, Cal.com or Acuity, with their details already filled in.' },
      availability: { title: 'WebHotelier Live Availability', desc: 'Answers with real availability and rates from the hotel\'s booking engine, not only a booking link.' },
      appointments: { title: 'Google Calendar appointments', desc: 'Books appointments straight into your calendar, seeing which times are free. Ideal for clinics, lawyers, service businesses.' },
      livechat: { title: 'Live chat by your team', desc: 'Someone from your team takes over the conversation from the dashboard, with reply suggestions from the AI. The "visitor asks for a person" alert works on every plan regardless.' },
      contactform: { title: 'Contact form in the chat', desc: 'A structured form inside the conversation (name, details, request) that reaches your team.' },
      sportsdata: { title: 'Sports results', desc: 'Live football data (API-Football): finds the match, per-half/period scores and statistics — so the Brain can explain a bet settlement from the official result.' },
      vision: { title: 'Reading images', desc: 'The visitor sends a photo or screenshot and the Brain reads what it shows — an error, a product, a document.' },
      prices: { title: 'Price comparison', desc: 'Shows visitors that booking direct is cheaper than Booking.com, Expedia and others — a balloon next to the chat and a comparison for their dates inside the conversation. Hotels with a booking engine only.' },
      reviews: { title: 'Reviews', desc: 'Shows your Google rating in the chat (for hotels also Tripadvisor and more) with the latest reviews, and the Brain answers with real numbers when asked "what do customers say". Refreshed weekly.' },
      ordertracking: { title: 'Order tracking', desc: 'A customer asks "where is my order?" and gets its live status, payment and shipping (courier and tracking number), after confirming with their email or phone. Works together with the eShop catalogue.' },
      catalog: { title: 'Product catalogue', desc: 'Searches your eShop live (WooCommerce / Vistoshop) and shows products with price, availability and a link.' },
      ticketing: { title: 'Ticketing', desc: 'When a person is needed, opens a ticket in your helpdesk (Zendesk or webhook) with the whole conversation attached.' },
      websearch: { title: 'Web Search', desc: 'When the answer is not in the Brain\'s knowledge, searches the web live — optionally only the sites you choose — and cites its sources.' },
    },
    viber: {
      intro: 'This Brain answers on Viber. Every Viber conversation appears under Conversations, and you can take one over from there — your reply is delivered on Viber.',
      testTitle: 'Test the Viber agent',
      testBody: 'Open your business\'s Viber bot on your phone and send a message. The reply comes from this Brain; the conversation shows up under Conversations within seconds.',
      openConversations: 'Open conversations',
    },
    email: {
      title: 'Email Channel (AI Auto-Reply & Drafts)',
      intro: 'Your Brain reads incoming emails over IMAP and drafts or sends replies over SMTP using the exact same knowledge, instructions, and tools (e.g. live WebHotelier availability, e-shop catalogue).',
      quotaBadge: 'Active mailboxes: {used} / {limit}',
      warmupPolicyTitle: 'Safety first — Mandatory 7-day Draft Warm-up',
      warmupPolicyBody: 'Every newly connected mailbox runs in "Draft" mode for its first 7 days: the AI saves its proposed reply in your email Drafts folder without emailing the customer, so you can verify quality in practice.',
      overLimitWarning: 'You have {used} enabled mailboxes while this Brain\'s limit is {limit}. Mailboxes are paused until the limit is increased or extra mailboxes are disabled.',
      onRequestNote: 'The Email channel is provisioned per mailbox. Contact us to enable mailbox slots on this Brain.',
      adminLimitLabel: 'Mailbox limit (Admin)',
      adminSaveLimit: 'Save limit',
      limitSaved: 'Mailbox limit updated.',
      mailboxesTitle: 'Connected Mailboxes',
      addMailbox: 'Add mailbox',
      noMailboxes: 'No mailbox connected yet.',
      newMailboxTitle: 'New mailbox',
      editMailboxTitle: 'Edit mailbox',
      statusEnabled: 'Enabled',
      statusDisabled: 'Disabled',
      inWarmupBadge: 'Warm-up (Draft until {date})',
      warmupUntil: 'Mandatory Draft warm-up until {date}',
      warmupCleared: '7-day warm-up cleared manually.',
      clearWarmupBtn: 'End warm-up',
      lastOk: 'Last check',
      lastError: 'Last error ({date}):',
      enableSwitch: 'Enabled',
      testImapBtn: 'Test IMAP',
      testSmtpBtn: 'Send test email',
      testImapOk: 'IMAP connection succeeded ({exists} messages in INBOX).',
      testSmtpOk: 'IMAP connection ({exists} messages) and test SMTP email to {email} succeeded.',
      pollDryBtn: 'Dry-run',
      pollLiveBtn: 'Check now',
      pollDryOk: 'Dry-run complete: inspected {fetched} messages (skipped {skipped}).',
      pollLiveOk: 'Check complete: {fetched} new · {replied} replied · {drafted} drafted · {escalated} escalated · {skipped} skipped.',
      saved: 'Mailbox saved.',
      deleted: 'Mailbox deleted.',
      deleteConfirm: 'Delete mailbox {address}?',
      recommended: 'recommended',
      modes: {
        smart_hybrid: 'Smart Hybrid (Auto-reply + Escalation)',
        draft: 'Draft Only (Save in IMAP Drafts)',
        auto_reply: 'Full Auto-Reply',
      },
      modeHints: {
        smart_hybrid: 'Replies automatically to standard questions (rates, availability, hours, info) and saves a Draft + notifies your team when there are attachments, complaints/refunds, low confidence, or a human request.',
        draft: 'Never sends an email on its own — saves every reply in your IMAP Drafts folder for your team to review and send.',
        auto_reply: 'Replies automatically to every eligible incoming email (unless the sender explicitly asks for a human).',
      },
      stats: {
        replied: 'Replied (month)',
        drafted: 'Drafted',
        escalated: 'Escalated',
        skipped: 'Skipped',
        failed: 'Failed',
      },
      fields: {
        label: 'Label',
        labelPh: 'e.g. Reservations / Support',
        address: 'Email address',
        fromName: 'Sender name (From)',
        fromNamePh: 'e.g. Hotel Aurora Reservations',
        mode: 'Reply mode',
        folder: 'IMAP folder',
        imapHost: 'IMAP Host',
        imapPort: 'IMAP Port',
        imapSecurity: 'IMAP Security',
        imapPortHint: 'Port 143 with STARTTLS is recommended.',
        smtpHost: 'SMTP Host',
        smtpPort: 'SMTP Port',
        username: 'Username',
        password: 'Password',
        passwordKeep: 'Saved — leave blank to keep current password',
        replyTo: 'Reply-To (optional)',
        cc: 'Cc addresses (comma-separated)',
        maxPerDay: 'Max replies/day per sender',
        hoursFrom: 'Hours from (HH:MM)',
        hoursTo: 'Hours to (HH:MM)',
        timezone: 'Timezone',
        blockedSenders: 'Blocked senders / domains (one per line)',
        blockedSendersHint: 'Auto-replies, newsletters, noreply@, mailer-daemon@ and the mailbox address itself are skipped automatically.',
        signatureText: 'Signature (plain text)',
        signatureTextPh: 'Best regards,\nOur team',
        signatureHtml: 'Signature (HTML, optional)',
      },
      eventsTitle: 'Recent Email Activity',
      eventsPrivacyNote: 'Only metadata is logged here (subject, sender domain, status). The conversation transcript lives under Conversations and follows your data retention policy.',
      allStatuses: 'All statuses',
      noEvents: 'No email events recorded yet.',
      colDate: 'Date',
      colMailbox: 'Mailbox',
      colSubject: 'Subject',
      colSender: 'Sender',
      colStatus: 'Status',
      colReason: 'Details',
    },
    instructions: {
      title: 'Central instructions',
      intro: 'The Brain\'s central training. It applies on every channel — the website, Viber and any you add. Per-language instructions live under Languages.',
      perAgentTitle: 'Extra instructions per channel or skill',
      perAgentIntro: 'These also have instructions of their own, which apply only there (e.g. only on Viber) on top of the central ones. Click to view or change them.',
      perAgentHint: 'Need something to apply only on one channel or skill, e.g. shorter answers only on Viber? Write it on that channel or skill page; it is added to the central instructions only there.',
    },
    languages: { intro: 'The Brain\'s languages. For each one: instructions specific to that language and where it learns from (site, sitemap).' },
    texts: { intro: 'What the visitor sees in the widget, per language: start message, placeholder, teaser, GDPR text.' },
    leads: { intro: 'Picks up email, phone and name from the conversation and notifies your team.' },
    test: {
      title: 'Test the Website agent',
      intro: 'The widget exactly as your visitors see it. Test conversations appear under Conversations.',
      locale: 'Language',
      restart: 'New conversation',
    },
  },
  companies: {
    billing: {
      heading: 'Partner billing',
      note: 'How the bots beneath this company are paid for. Applies to bots created from now on; existing bots are not converted.',
      label: 'Billing (if this company is a partner)',
      none: 'Not a partner',
      vistoweb: 'Invoiced by Vistoweb (monthly)',
      paddle: 'Through Paddle (self-serve)',
      createHint: 'Invoiced by Vistoweb: the partner picks Free / Pro / Max per bot and receives our monthly invoice.',
    },
    create: {
      cta: '+ New company',
      title: 'New company',
      name: 'Company name',
      namePlaceholder: 'e.g. Hotel Aurora',
      nameRequired: 'Enter the company name.',
      parent: 'Under',
      ownerHint: 'The company opens held by the company above it. Invite the customer from Members and transfer ownership when they are ready.',
      invoicingHeading: 'Invoicing details (optional)',
      submit: 'Create company',
      cancel: 'Cancel',
      exists: 'A company with this name already exists here.',
      forbidden: 'A new company can only be opened under a company with an agreed price list.',
    },
    locks: {
      heading: 'Self-service',
      note: 'Switch off what this company may change for itself. Conversations, leads and analytics are never locked.',
      readonlyNote: 'Set by the company above this one.',
      area: { bot_settings: 'Bot settings', knowledge: 'Knowledge', team: 'Team' },
      hint: {
        bot_settings: 'Locked: they cannot change the prompt, model or widget.',
        knowledge: 'Locked: they cannot change knowledge sources or reindex.',
        team: 'Locked: they cannot invite or remove members.',
      },
    },
    prices: {
      heading: 'What this company pays, per plan',
      note: 'Per bot, per month. A bot already linked keeps the price it was linked at — a new price applies to new links only.',
      unset: 'Not set',
      edit: 'Edit',
      editNote: 'The new price applies to bots linked from this date. Those already linked keep the price frozen on them, which is what makes a price change safe to schedule.',
      scheduled: 'Changes to {amount} on {date}',
      amount: 'Amount per bot / month (EUR)',
      effectiveFrom: 'Effective from',
      noteLabel: 'Internal note',
      invalidAmount: 'Enter a valid amount.',
      noPriceList: 'Saved. Nobody at or above this company has a price list, so the amount is left unset — set it by hand in Managed billing.',
      noListEntry: 'Saved, but your price list has no entry for {plan} — the amount is left unset rather than shown as 0. Set the price above.',
    },
    includes: {
      languages: '{value} languages',
      responses_per_month: '{value} responses / month',
      responses_unlimited: 'Unlimited responses',
      analytics: 'Analytics — {value} days of history',
      analytics_export: 'CSV export',
      analytics_topics: 'Top user questions',
      white_label: 'White label (no VistoChat branding)',
      discreet_branding: 'Discreet branding',
      api_and_webhooks: 'API and webhooks',
      rag_smart_search: 'Smart search over the knowledge base',
      web_search: 'Web search',
      booking_api: 'Live availability and rates',
      priority_support: 'Priority support',
    },
    caveats: { webhotelier_only: '(WebHotelier engines only)' },
  },

  admin: {
    feedback: {
      tabFeedback: "Feedback",
      tabProposals: "Prompt proposals",
      pickBot: "Pick a bot…",
      generateBtn: "Generate proposal",
      modelSummary: "What the model found",
      sectionLabel: "Text that will be written",
      writesToBase: "base prompt",
      writesToLocales: "per-locale prompts ({l})",
      reviewWarning: "Read this before approving. The model can misread an injection attempt as a real incident, or generalise beyond what the feedback actually said. Edit freely — what is in the box is exactly what gets written, and only inside the marker block.",
      applyBtn: "Approve & apply",
      rejectBtn: "Reject",
      confirmApply: "Write this section into the prompt of {bot}?",
      applyError: "Could not apply.",
      digestError: "Could not generate a proposal.",
      noProposals: "No proposals.",
      fromN: "from {n} items",
      backupAt: "Backup:",
      statusLabel: "Status",
      proposalStatus: { draft: "Drafts", applied: "Applied", rejected: "Rejected" },
      title: 'Customer feedback',
      subtitle: 'Notes and corrections customers left on conversations. Corrections carry the bot answer next to what it should have said — that pair is what fixes a prompt.',
      botSaid: 'Bot answered',
      customerSaid: 'Customer says',
      internalPlaceholder: 'Internal note (customer never sees this) — blur to save',
      searchPlaceholder: 'Search in notes…',
      count: '{n} total',
      empty: 'Nothing in this queue.',
      loadError: 'Could not load the queue.',
      saveError: 'Could not save.',
      filters: {
        open: 'Open', reviewed: 'Reviewed', applied: 'Applied', dismissed: 'Dismissed',
        all: 'All statuses', allKinds: 'All kinds',
      },
    },
    common: {
      onlyAdmin: 'Vistoweb admins only.',
      errorAccess: 'You don\'t have admin access.',
      errorLoad: 'Loading error.',
      loading: 'Loading…',
      saving: 'Saving…',
      close: 'Close',
    },
    users: {
      title: 'Users',
      subtitle: 'Every system user, with their teams, roles and last login.',
      searchPlaceholder: 'Search by name, email or team…',
      showing: '{n} of {total}',
      noMatch: 'No user matches your search.',
      stats: { total: 'Total', verified: 'Verified', loggedIn: 'Logged in', admins: 'Admins' },
      cols: { user: 'User', teams: 'Teams / Roles', verified: 'Verified', lastLogin: 'Last login' },
    },
    companies: {
      heading: 'Companies',
      subtitle: 'Every company you operate: its invoicing details, its people, its bots, and what it may change for itself.',
      searchPlaceholder: 'Search company, VAT or owner…',
      rowCount: '{n} companies',
      manageBtn: 'Manage',
      empty: 'No companies found.',
      summary: {
        companies: 'Companies',
        members: 'Total members',
        bots: 'Total bots',
      },
      table: {
        team: 'Team',
        legalName: 'Legal name',
        vat: 'VAT',
        owner: 'Owner',
        members: 'Members',
        bots: 'Bots',
        sellsTo: 'sells to {n}',
      noLegal: '— not set —',
      },
      identity: {
        heading: 'Invoicing details',
        hint: 'The invoiced legal entity. Used for Paddle B2B reverse-charge and manual invoices.',
        legalName: 'Legal name (entity)',
        legalNamePlaceholder: 'e.g. CRYSTAL VOULIAGMENI HOTEL IKE',
        address: 'Address',
        addressPlaceholder: 'e.g. 10 Dekeleias & Danais, 166 71',
        vat: 'VAT number',
        country: 'Country (ISO-2)',
        billingEmail: 'Billing email',
        save: 'Save details',
        saved: 'Saved',
        errorSave: 'Could not save details.',
      },
      bots: {
        heading: 'Bots',
        none: 'This company has no bots yet.',
        bot: 'Bot',
        status: 'Status',
        plan: 'Plan',
        chunks: 'Chunks',
        monthly: 'Monthly (ex VAT)',
        open: 'Open',
        extHint: 'Invoiced by Vistoweb outside Paddle',
      },
      members: {
        heading: 'Team members',
        user: 'User',
        role: 'Role',
        lastLogin: 'Last login',
        makeOwner: 'Make owner',
        remove: 'Remove',
        resetPassword: 'Reset password',
        pending: 'Pending invitations',
        copyLink: 'Copy link',
        copyPass: 'Copy',
        copied: 'Copied ✓',
        revoke: 'Revoke',
        invitePlaceholder: 'new member email',
        invite: 'Invite',
        inviteHint: 'The invitation is emailed; the member joins the team once they accept it.',
        confirmOwner: 'Transfer ownership to {email}? The current owner becomes an admin.',
        confirmRemove: 'Remove {email} from the team?',
        confirmRevoke: 'Revoke the invitation to {email}?',
        confirmReset: 'Generate a new password for {email}? All open sessions will be signed out.',
        resetDone: 'New password for {email} — copy it now, it won\'t be shown again:',
        resetWarn: 'Share it with the customer securely. The user must sign in again with this password.',
        errorAction: 'The action failed.',
        errorInvite: 'The invitation failed.',
        roles: {
          owner: 'Owner',
          admin: 'Admin',
          editor: 'Editor',
          agent: 'Agent',
          member: 'Member',
          viewer: 'Viewer',
        },
      },
    },
    stats: {
      title: 'System stats',
      heading: 'Admin · System stats',
      subtitle: 'Vistoweb internal — revenue, cost, margin & churn signals across all teams.',
      money: {
        mrr: 'MRR (Paddle)',
        mrrSub: 'monthly recurring (Paddle subs)',
        outOfBand: 'Out-of-band (Vistoweb)',
        outOfBandSub: '{n} bots · manual invoicing/mo',
        totalMonthly: 'Revenue (monthly)',
        totalMonthlySub: 'Paddle MRR + out-of-band',
        paidWindow: 'Paid in window',
        paidWindowSub: '{days} days · actual Paddle cash collected',
        llmCost: 'LLM cost (window)',
        llmCostSub: 'tokens consumed across all bots',
        grossMargin: 'Gross margin (monthly est.)',
        grossMarginSub: 'monthly revenue − monthly LLM cost (run-rate)',
      },
      stats: {
        chats: 'Conversations',
        leads: 'Leads',
        conversion: 'Conversion',
        messages: 'Messages',
        teams: 'Teams',
        teamsSub: '{paid} paid · {trialing} trial · {atRisk} at risk',
      },
      planDistribution: 'Plan distribution',
      planTotal: '{n} bots total',
      subHealth: 'Subscription health',
      perTeam: 'Per-team revenue / cost / margin',
      perTeamMeta: '{n} teams with activity',
      noActivity: 'No activity in this range.',
      topBots: 'Top bots by LLM cost',
      topBotsMeta: 'top 20 — see who\'s burning tokens',
      noTokenCost: 'No token charges in this range.',
      table: {
        team: 'Team',
        bots: 'Bots',
        chats: 'Chats',
        leads: 'Leads',
        cost: 'Cost',
        revenue: 'Revenue',
        margin: 'Margin',
        bot: 'Bot',
        plan: 'Plan',
        msgs: 'Msgs',
      },
      sub: {
        none: 'no sub',
        trial: 'trial',
        active: 'active',
        pastDue: 'past_due',
        paused: 'paused',
        cancelled: 'cancelled',
        expired: 'expired',
      },
      extPillTitle: 'Vistoweb manual invoice',
    },
    managed: {
      title: 'Managed billing',
      heading: 'Admin · Managed subscriptions',
      subtitle: 'Vistoweb invoiced — billing terms, amounts excl. VAT, invoicing dates. Does not go through Paddle.',
      summary: {
        managedBots: 'Managed bots',
        managedBotsSub: 'externally invoiced',
        monthlyEx: 'Monthly total (excl. VAT)',
        monthlyExSub: 'net recurring',
        monthlyInc: 'Monthly total (incl. VAT)',
        monthlyIncSub: 'gross invoiced',
      },
      reminders: {
        overdueTitleOne: '{n} bot OVERDUE',
        overdueTitleMany: '{n} bots OVERDUE',
        todayTitleOne: 'To invoice today: {n} customer',
        todayTitleMany: 'To invoice today: {n} customers',
        weekTitle: 'Next 7 days: {n} invoices',
        total: 'Total: {ex} excl. VAT · {inc} incl. VAT',
        daysLateOne: '{n} day late',
        daysLateMany: '{n} days late',
        exVatSuffix: 'excl. VAT',
      },
      filter: {
        managedOnly: 'Managed only',
        all: 'All bots',
        rowCount: '{n} entries',
      },
      table: {
        customer: 'Customer / Bot',
        plan: 'Plan',
        monthlyEx: 'Monthly (excl. VAT)',
        monthlyInc: 'Incl. VAT',
        setup: 'Setup',
        billing: 'Invoicing',
        startedAt: 'Started',
        nextInvoice: 'Next',
        notes: 'Notes',
        billingDay: 'Day {n}',
        listPrice: 'list price',
        vatPctSuffix: '% VAT',
        empty: 'No entries.',
        editBtn: 'Edit',
      },
      modal: {
        editAria: 'Edit {team} · {bot}',
        closeAria: 'Close',
        manageExt: 'Managed outside Paddle (Vistoweb invoicing)',
        manageExtHint: 'Active = the bot is invoiced manually by Vistoweb. Paddle upgrade/cancel disabled.',
        plan: 'Plan',
        monthly: 'Monthly amount excl. VAT (€)',
        monthlyPlaceholder: 'empty = list price',
        monthlyHint: 'Leave empty to use the plan\'s default price.',
        setup: 'Setup excl. VAT (€)',
        vat: 'VAT %',
        billingDay: 'Billing day (1-28)',
        billingDayPlaceholder: 'e.g. 5',
        billingDayHint: 'Capped at 28 to avoid February issues.',
        startedAt: 'Start date',
        notes: 'Notes (admin-only)',
        notesPlaceholder: 'E.g. initial offer, contact person, special terms…',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        errorSave: 'Save failed.',
      },
    },
    usage: {
      title: 'Usage',
      heading: 'Admin · Usage & Costs',
      subtitle: 'Vistoweb internal — credits consumption per bot, model, provider. Helps spot overusage and evaluate models.',
      coverage: {
        label: 'Coverage:',
        coveragePct: '{covered} / {total} assistant messages have cost data ({pct}%).',
        legacyEstimate: '{n} legacy WP messages: estimated via blended rate (80% input / 20% output).',
        dateRange: 'Data: {from} → {to}.',
      },
      kpi: {
        totalCost: 'AI model cost (window)',
        totalCostSub: '{days} days',
        avgDay: 'Avg per day',
        avgDaySub: 'avg cost/day',
        projected: 'Projected/month',
        projectedSub: 'based on current rate',
        activeBots: 'Active bots',
        activeBotsSub: '{messages} messages',
      },
      costPerDay: 'Cost per day (stacked by provider)',
      noData: 'No data in the selected range.',
      chroma: {
        heading: '🟣 Chroma Cloud — Starter (usage-based)',
        chunks: 'chunks stored',
        chunksSub: '~13 MiB storage in 9 collections',
        reads: 'reads (in window)',
        writes: 'writes ({n} reindex events)',
        manual: 'manual',
        manualLabel: 'cost source',
        manualSub: 'see actual invoices below',
      },
      pnl: {
        heading: '📊 Result per month — revenue and full cost',
        intro: 'Revenue from the books (service period). Cost: invoices where available, otherwise metered usage or estimate. Prepayments (DataForSEO, DeepSeek) are NOT cost — consumption is.',
        month: 'Month', revenue: 'Revenue', cost: 'Cost', breakdown: 'Cost breakdown', margin: 'Margin',
        current: 'current', total: 'Total', lines: '{n} lines · {inv} invoiced',
        prepaid: 'Prepayment (not cost)',
        src: { invoice: 'from invoice', metered: 'metered consumption', estimate: 'estimate from usage', carried: 'not invoiced yet — previous month amount' },
        srcShort: { invoice: 'I', metered: 'M', estimate: 'E', carried: '≈' },
        legend: 'I = invoice · M = metered consumption · E = estimate · ≈ not invoiced yet (previous month amount).',
        accountsHeading: '👥 Per customer — revenue and variable cost of its Brains (since January)',
        accountsIntro: 'Variable cost = AI models + embeddings + DataForSEO of the account Brains. Infrastructure (server, Chroma) is shared and not allocated.',
        customer: 'Customer', brains: 'Brains', variableCost: 'Variable cost',
      },
      reconciliation: {
        heading: '🧾 Estimate vs Reality — per month/provider',
        estimated: 'Estimate (window)',
        actual: 'Actual (from invoices)',
        variance: 'Variance',
        table: {
          month: 'Month',
          provider: 'Provider',
          estimated: 'Estimate',
          actual: 'Actual',
          variance: 'Variance',
          pct: '%',
        },
      },
      invoices: {
        heading: '📋 Provider invoices (manual entry)',
        amountPlaceholder: 'EUR',
        invoiceRefPlaceholder: 'Invoice #',
        notesPlaceholder: 'Notes (optional)',
        submit: 'Submit',
        empty: 'No invoices entered yet.',
        confirmDelete: 'Delete? Cannot be undone.',
        table: {
          month: 'Month',
          provider: 'Provider',
          amount: 'Amount (EUR)',
          original: 'Original',
          ref: 'Invoice #',
          notes: 'Notes',
        },
      },
      perModel: {
        heading: 'Per model',
        provider: 'Provider',
        model: 'Model',
        bots: 'Bots',
        messages: 'Messages',
        inputTokens: 'Input tokens',
        outputTokens: 'Output tokens',
        cost: 'Cost',
        pctTotal: '% of total',
      },
      perProvider: {
        heading: 'Per provider',
        provider: 'Provider',
        messages: 'Messages',
        inputTokens: 'Input tokens',
        outputTokens: 'Output tokens',
        cost: 'Cost',
        pctTotal: '% of total',
      },
      topBots: {
        heading: 'Top {n} bots by cost',
        full: 'Full bot list ({n})',
        bot: 'Bot',
        team: 'Team',
        plan: 'Plan',
        messages: 'Messages',
        input: 'Input',
        output: 'Output',
        cost: 'Cost',
      },
      pricing: {
        heading: '📋 Pricing catalog ({n} models)',
        provider: 'Provider',
        model: 'Model',
        inputPer: 'Input €/1M',
        outputPer: 'Output €/1M',
        cacheHit: 'Cache hit',
        effectiveFrom: 'Effective from',
        notes: 'Notes',
        footnote: 'All prices in micro-EUR (1e-6 EUR). USD→EUR conversion at 0.93 (snapshot 2026-04-28). For updates, edit the model_pricing table directly.',
      },
    },
    contactRequests: {
      title: 'Contact requests',
      heading: 'Contact requests',
      subtitle: '{n} requests from the vistochat.com form — admin only.',
      filterAll: 'All',
      searchPlaceholder: 'Email, name, company, phone, message…',
      empty: {
        heading: 'No contact requests',
        body: 'When visitors submit the form on vistochat.com, they appear here.',
      },
      table: {
        details: 'Details',
        message: 'Message',
        source: 'Source',
        captured: 'Captured',
        status: 'Status',
        followup: 'Followup',
      },
      pager: {
        prev: '‹ Prev',
        next: 'Next ›',
        info: 'Page {page} / {total} · {n} total',
      },
      detail: {
        ariaLabel: 'Contact request',
      },
      drawer: {
        contactInfo: 'Contact details',
        labelName: 'Name',
        labelEmail: 'Email',
        labelPhone: 'Phone',
        labelCompany: 'Company',
        labelWebsite: 'Website',
        labelIp: 'IP',
        labelSource: 'Source',
        labelSourceUrl: 'Source URL',
        labelNotify: 'Notify',
        notifySent: '✓ {date}',
        notifyNotSent: '— (not sent)',
        message: 'Message',
        statusNotes: 'Status & Notes',
        statusLabel: 'Status',
        statusNote: 'Status note (optional)',
        internalNotes: 'Internal notes',
        followupDate: 'Followup date',
        delete: 'Delete',
        save: 'Save changes',
        saving: 'Saving…',
        closeAria: 'Close',
        confirmDelete: 'Delete the request from "{label}"? This action cannot be undone.',
        unknown: 'unknown',
      },
      errors: {
        accessDenied: 'Admins only.',
        loadError: 'Loading error',
        saveError: 'Save error',
        deleteError: 'Deletion error',
      },
      status: {
        new: 'New',
        contacted: 'Contacted',
        qualified: 'Qualified',
        closed: 'Closed',
        lost: 'Lost',
        spam: 'Spam',
      },
    },
  },
  verticals: {
    fieldLabel: 'Kind of business',
    column: 'Category',
    fieldHint: 'Decides which skills the catalogue shows first. It does not switch anything on or off.',
    hiddenNote: '{n} skills are not usually relevant to “{kind}” and are hidden.',
    showAll: 'Show all',
    showFitting: 'Only the relevant ones',
    general: { label: 'General (all skills)', short: 'General' },
    hotel: { label: 'Hotel / accommodation', short: 'Hotel' },
    store: { label: 'Shop / e-shop', short: 'Shop' },
    services: { label: 'Services / professionals', short: 'Services' },
    betting: { label: 'Betting / iGaming', short: 'Betting' },
  },
  plugins: {
    title: 'Plugins',
    subtitle: 'Every Brain and its channels/skills in one table: what runs where, what the plan still allows and what needs attention. Click a cell to configure it.',
    loading: 'Loading…',
    loadError: 'Could not load.',
    search: 'Search Brains…',
    allCategories: 'All categories',
    company: 'Company',
    allCompanies: 'All companies',
    allColumns: 'All columns',
    count: '{n} Brains',
    empty: 'No Brain matches the filters.',
    colBrain: 'Brain',
    colCustom: 'Custom',
    attentionTitle: 'Needs attention ({n})',
    state: {
      active: 'Active',
      issue: 'Active — needs attention',
      overLimit: 'On but beyond the plan — NOT working',
      available: 'Included in the plan — off',
      upgrade: 'Needs a bigger plan',
      requires: 'Needs another plugin first',
      none: 'Not relevant to this category',
    },
    issues: {
      over_limit: 'On but beyond the plan\'s slots — not working.',
      reviews_stale: 'Reviews not refreshed for {days} days.',
      reviews_error: 'The last reviews refresh failed.',
      prices_stale: 'Price measurement not refreshed for {days} days.',
      prices_claim_hidden: 'Price measurement is old ({days} days) — the "usually N% cheaper" claim is hidden.',
      prices_error: 'The last price measurement failed.',
      booking_no_url: 'A booking engine is selected but has no link.',
      catalog_no_url: 'A product catalogue is selected but has no connection URL.',
      email_error: 'The last email mailbox (IMAP/SMTP) check failed.',
    },
  },
};
